import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../services/api';
import { Check, Info, AlertTriangle, ChevronRight, Power, Circle, ChevronDown, Maximize2, Database, Eye, Moon, Trash2, Camera, CreditCard, Plus, Server, Key, Folder, Image, LogOut } from 'lucide-react';
import FirewallDetailsModal from '../components/Firewall/FirewallDetailsModal';
import { useNotification } from '../Notifications';
import { useAuth } from '../components/auth/AuthContext';

import ResponsiveWrapper from '../components/vm/ResponsiveWrapper';

import {
  StartConfirmationModal,
  StopConfirmationModal,
  RebootConfirmationModal,
  RestoreConfirmationModal
} from './DashboardComponents';

import { 
  API_BASE_URL, 
  GPU_PRICING, 
  sanitizeText, 
  getSafeErrorMessage, 
  currencyUtils,
  useResponsive,
  logError,
  logInfo,
  logUserAction,
  logPerformance,
  cacheVMOperation,
  clearVMOperationCache,
  getVMOperationCache,
  ERROR_MESSAGES,
getNetworkErrorMessage  // ADD THIS

} from './dashboardUtils';
const truncateText = (text, maxLength = 1000) => {
  if (!text || typeof text !== 'string') return '';
  return text.substring(0, maxLength);
};

// Helper function to detect advanced regions
const isAdvancedRegion = (vm) => {
  const advancedRegions = [
    "FINLAND-1", "FINLAND-2", "FINLAND-3",
    "FIN-01", "FIN-02", "FIN-03", "ICE-01"
  ];
  return advancedRegions.includes(vm?.region);
};

const canStartVM = (vm) => {
  if (!vm || !vm.status) return false;
  const status = vm.status;
  return status === 'SHUTOFF';
};

const canStopVM = (vm) => {
  if (!vm || !vm.status) return false;
  const status = vm.status;
  return status === 'RUNNING';
};

const canRebootVM = (vm) => {
  if (!vm || !vm.status) return false;
  const isAdvanced = isAdvancedRegion(vm);
  const status = vm.status;
  return !isAdvanced && status === 'RUNNING';
};

const canRestoreVM = (vm) => {
  if (!vm || !vm.status) return false;
  const isAdvanced = isAdvancedRegion(vm);
  const status = vm.status;
  return !isAdvanced && status === 'HIBERNATED';
};

const canHibernateVM = (vm) => {
  if (!vm || !vm.status) return false;
  const isAdvanced = isAdvancedRegion(vm);
  const status = vm.status;
  
  if (isAdvanced) {
    return status === 'RUNNING' || status === 'SHUTOFF';
  } else {
    return status === 'RUNNING' || status === 'SHUTOFF';
  }
};

const canDeleteVM = (vm) => {
  if (!vm || !vm.status) return false;
  const validStatuses = ['RUNNING', 'SHUTOFF', 'HIBERNATED'];
  return validStatuses.includes(vm.status);
};

const canModifyPublicIP = (vm) => {
  if (!vm || !vm.status) return false;
  const isAdvanced = isAdvancedRegion(vm);
  const status = vm.status;
  return !isAdvanced && status === 'RUNNING';
};

const canAddSecurityRule = (vm) => {
  if (!vm || !vm.status) return false;
  const isAdvanced = isAdvancedRegion(vm);
  const status = vm.status;
  return !isAdvanced && status === 'RUNNING';
};
// VMDetailsPage Component - FULLY UPDATED with ALL Patterns
const VMDetailsPage = ({ vmId, onBack, userCurrency = currencyUtils.defaultCurrency }) => {
    const { isMobile, isTablet } = useResponsive();
    const notification = useNotification();
    const { handleAuthError } = useAuth();

    // State variables
    const [vmDetails, setVmDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isTogglingIP, setIsTogglingIP] = useState(false);
    const [ipActionSuccess, setIpActionSuccess] = useState(null);
    const [showAddRuleModal, setShowAddRuleModal] = useState(false);
    const [newRule, setNewRule] = useState({
        direction: 'ingress',
        protocol: 'tcp',
        ethertype: 'IPv4',
        remote_ip_prefix: '0.0.0.0/0',
        port_range_min: '',
        port_range_max: ''
    });
    const [useSpecificPorts, setUseSpecificPorts] = useState(false);
    const [isAddingRule, setIsAddingRule] = useState(false);
    const [isDeletingRule, setIsDeletingRule] = useState(null);
    const [addRuleError, setAddRuleError] = useState(null);
    const [lastShownAddRuleError, setLastShownAddRuleError] = useState('');
    const [ruleActionStatus, setRuleActionStatus] = useState(null);
    const [attachedFirewalls, setAttachedFirewalls] = useState([]);
    const [isLoadingFirewalls, setIsLoadingFirewalls] = useState(false);
    const [selectedFirewall, setSelectedFirewall] = useState(null);
    const [showFirewallModal, setShowFirewallModal] = useState(false);
    const [error, setError] = useState(null);
    const [lastShownError, setLastShownError] = useState('');
    const [actionError, setActionError] = useState(null);
    const [lastShownActionError, setLastShownActionError] = useState('');
    const [ipActionError, setIpActionError] = useState(null);
    const [lastShownIpActionError, setLastShownIpActionError] = useState('');
    const [firewallError, setFirewallError] = useState(null);
    const [lastShownFirewallError, setLastShownFirewallError] = useState('');

    // PATTERN 3: Granular loading states with Sets
    const [loadingStates, setLoadingStates] = useState({
        starting: new Set(),
        stopping: new Set(),
        rebooting: new Set(),
        restoring: new Set()
    });

    // Modal state using VM objects instead of booleans
    const [selectedVmForStart, setSelectedVmForStart] = useState(null);
    const [selectedVmForStop, setSelectedVmForStop] = useState(null);
    const [selectedVmForReboot, setSelectedVmForReboot] = useState(null);
    const [selectedVmForRestore, setSelectedVmForRestore] = useState(null);
    const [selectedRuleForDelete, setSelectedRuleForDelete] = useState(null);


    const [actionSuccess, setActionSuccess] = useState(null);
    const [usagePeriod, setUsagePeriod] = useState('hour');
    const [usageAmount, setUsageAmount] = useState(1);
    const [inputValue, setInputValue] = useState('1');
const [inputFocused, setInputFocused] = useState(false);
const [isInputValid, setIsInputValid] = useState(true);

    // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
        const [isRefreshing, setIsRefreshing] = useState(false);
    const [isManualRefresh, setIsManualRefresh] = useState(false);

    // PATTERN 4: Locked VMs state for optimistic UI
    const [lockedVms, setLockedVms] = useState(new Set());

    const navigate = useNavigate();
    const isFetchingVmDetails = useRef(false);
      const isFetchingFirewalls = useRef(false);
    const abortControllerRef = useRef(null);
    const fetchTimeoutRef = useRef(null);

    // PATTERN 3: Ref-based duplicate prevention
    const operationRefs = useRef({
        starting: new Set(),
        stopping: new Set(),
        rebooting: new Set(),
        restoring: new Set(),
        togglingIP: new Set(),
        addingRule: new Set(),
        deletingRule: new Set()
    });

    // PATTERN 7: Retry logic formalization
    const maxRetries = 3;
    const retryAttempts = useRef(0);

    // Component lifecycle logging
   useEffect(() => {
        // Create new abort controller on mount
        abortControllerRef.current = new AbortController();
        
        logInfo('VMDetailsPage component mounted', {
            vmId,
            isMobile,
            isTablet,
            userCurrency
        });

        return () => {
            logInfo('VMDetailsPage component unmounted', {
                vmId,
                hadVmDetails: !!vmDetails
            });
            
            // Abort any in-flight requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            
            // Clear any pending timeouts
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
                fetchTimeoutRef.current = null;
            }
        };
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const calculateVmCost = useMemo(() => {
        if (!vmDetails || !vmDetails.specs || !vmDetails.specs.gpu_type) {
            return { hourly: 0, monthly: 0, currency: userCurrency, symbol: currencyUtils.getSymbol(userCurrency) };
        }

        const currency = userCurrency;
        const currencySymbol = currencyUtils.getSymbol(currency);
        
        const gpuType = vmDetails.specs.gpu_type;
        const gpuCount = vmDetails.specs.gpu_count || 1;
            
        let hourlyRate = 0;
        
        if (GPU_PRICING[gpuType]) {
            hourlyRate = GPU_PRICING[gpuType].price[currency] * gpuCount;
        } else {
            for (const [key, value] of Object.entries(GPU_PRICING)) {
                if (gpuType.includes(key) || key.includes(gpuType)) {
                    hourlyRate = value.price[currency] * gpuCount;
                    break;
                }
            }
        }
        
        const monthlyRate = hourlyRate * 730;
        
        return {
            hourly: hourlyRate,
            monthly: monthlyRate,
            currency,
            symbol: currencySymbol
        };
    }, [vmDetails?.specs?.gpu_type, vmDetails?.specs?.gpu_count, userCurrency]);

    const calculatedValues = useMemo(() => {
        const hours = (() => {
            switch(usagePeriod) {
                case 'hour': return usageAmount;
                case 'day': return usageAmount * 24;
                case 'month': return usageAmount * 730;
                case 'year': return usageAmount * 8760;
                default: return usageAmount;
            }
        })();
        
        return {
            usageHours: hours,
            totalCost: calculateVmCost.hourly * hours,
            needsCustomPricing: hours >= 730 || usagePeriod === 'month' || usagePeriod === 'year'
        };
    }, [usagePeriod, usageAmount, calculateVmCost.hourly]);


const fetchVmDetails = async (isManual = false) => {
    if (isFetchingVmDetails.current) return;
    
    if (initialDataLoaded) {
        setIsRefreshing(true);
        setIsManualRefresh(isManual);
    }
    
    const cachedOp = getVMOperationCache();
    if (cachedOp && cachedOp.vmId === vmId) {
        setVmDetails(prev => prev ? {...prev, status: cachedOp.status} : prev);
        
        logInfo('Applied cached VM operation in details', {
            vmId: cachedOp.vmId,
            status: cachedOp.status
        });
    }
    
    if (retryAttempts.current >= maxRetries) {
        setError('Maximum refresh attempts reached. Please wait before trying again.');
        logError('Maximum VM details fetch attempts exceeded', {
            attempts: retryAttempts.current,
            maxRetries,
            vmId
        });
        return;
    }
    
    const startTime = performance.now();
    retryAttempts.current++;
    
    try {
        isFetchingVmDetails.current = true;
        setIsLoading(true);
        
        logInfo('Fetching VM details', {
            vmId,
            initialLoad: !initialDataLoaded,
            attempt: retryAttempts.current
        });
        
        const response = await api.getBarrackVMDetails(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setVmDetails(response.instance);
            clearVMOperationCache();
            setIsRefreshing(false);
            setIsManualRefresh(false);
            
            if (!isAdvancedRegion(response.instance)) {
                fetchAttachedFirewalls(vmId);
            }
            
            if (!initialDataLoaded) {
                setInitialDataLoaded(true);
            }

            retryAttempts.current = 0;
            logPerformance('fetch_vm_details', duration, {
                success: true,
                vmId,
                vmName: response.instance?.name,
                vmStatus: response.instance?.status
            });
            
            logInfo('VM details fetched successfully', {
                vmId,
                vmName: response.instance?.name,
                vmStatus: response.instance?.status,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            throw new Error(response.error || 'Failed to fetch VM details');
        }
    } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        setIsLoading(false);
        isFetchingVmDetails.current = false;
        return;
    }
    
    // Network error (no status means request never reached server)
    if (!err.status && err.code) {
        setError(err.error);
        logError('Network error in fetchVmDetails', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId
        }, err);
        setIsLoading(false);
        isFetchingVmDetails.current = false;
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(err.error);
        setIsLoading(false);
        isFetchingVmDetails.current = false;
        return;
    }
    
    // VM not found
    if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
        notification.error('This VM no longer exists');
        setIsLoading(false);
        isFetchingVmDetails.current = false;
        onBack();
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('VM details fetch failed', {
            code: err.code,
            vmId,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        setIsLoading(false);
        isFetchingVmDetails.current = false;
        return;
    }
    
    // Fallback
    setError(err.error || 'An unexpected error occurred');
    
    logPerformance('fetch_vm_details', duration, {
        success: false,
        error: err.error
    });
    
    logError('VM details fetch failed', {
        vmId,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
}finally{
        setIsLoading(false);
        setIsRefreshing(false);
        setIsManualRefresh(false);
        isFetchingVmDetails.current = false;
    }
};
      const debouncedFetchVmDetails = useCallback((delay = 300, isManual = false) => {
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }
        
        fetchTimeoutRef.current = setTimeout(() => {
            if (!isFetchingVmDetails.current) {
                logInfo('Debounced VM details fetch triggered', { delay, vmId, isManual });
                fetchVmDetails(isManual);
            }
            fetchTimeoutRef.current = null;
        }, delay);
    }, [vmId]);    // Refresh functions for focus/visibility events



    // Toast notification de-duplication pattern
    useEffect(() => {
        if (error && error !== lastShownError) {
            notification.error(error);
            setLastShownError(error);
            setError(null);
        }
    }, [error, notification, lastShownError]);

    useEffect(() => {
        if (actionError && actionError !== lastShownActionError) {
            notification.error(actionError);
            setLastShownActionError(actionError);
            setActionError(null);
        }
    }, [actionError, notification, lastShownActionError]);

    useEffect(() => {
        if (ipActionError && ipActionError !== lastShownIpActionError) {
            notification.error(ipActionError);
            setLastShownIpActionError(ipActionError);
            setIpActionError(null);
        }
    }, [ipActionError, notification, lastShownIpActionError]);

    useEffect(() => {
        if (firewallError && firewallError !== lastShownFirewallError) {
            notification.error(firewallError);
            setLastShownFirewallError(firewallError);
            setFirewallError(null);
        }
    }, [firewallError, notification, lastShownFirewallError]);

    useEffect(() => {
        if (addRuleError && addRuleError !== lastShownAddRuleError) {
            notification.error(addRuleError);
            setLastShownAddRuleError(addRuleError);
            setAddRuleError(null);
        }
    }, [addRuleError, notification, lastShownAddRuleError]);

    // Convert success messages to toast notifications
    useEffect(() => {
        if (actionSuccess) {
            notification.success(actionSuccess);
            setActionSuccess(null);
        }
    }, [actionSuccess, notification]);

    useEffect(() => {
        if (ipActionSuccess) {
            notification.success(ipActionSuccess);
            setIpActionSuccess(null);
        }
    }, [ipActionSuccess, notification]);

   useEffect(() => {
        if (!isFetchingVmDetails.current) {
            fetchVmDetails();
        }
        
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [vmId]);

    // Re-fetch when vmId changes (with debounce for rapid navigation)
    useEffect(() => {
        if (vmId) {
            debouncedFetchVmDetails(100);
        }
    }, [vmId, debouncedFetchVmDetails]);
    
    useEffect(() => {
        let timeout;
        if (actionSuccess || actionError) {
            timeout = setTimeout(() => {
                setActionSuccess(null);
                setActionError(null);
            }, 5000);
        }
        return () => clearTimeout(timeout);
    }, [actionSuccess, actionError]);

const fetchAttachedFirewalls = async (id) => {
    if (isFetchingFirewalls.current) return;

    if (isAdvancedRegion(vmDetails)) {
        setAttachedFirewalls([]);
        logInfo('Skipping firewall fetch for advanced region VM', {
            vmId: id,
            region: vmDetails?.region
        });
        return;
    }
    
    const startTime = performance.now();
    
    try {
        isFetchingFirewalls.current = true;
        setIsLoadingFirewalls(true);
        setFirewallError(null);
        
        logInfo('Fetching attached firewalls', { vmId: id });
        
        const response = await api.getVmAttachedFirewalls(id);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            if (response.attached_firewalls && response.attached_firewalls.length > 0) {
                const detailsPromises = response.attached_firewalls.map(firewall => 
                    api.getFirewallDetails(firewall.id)
                        .catch(detailErr => {
                            logError('Firewall details fetch failed', {
                                firewallId: firewall.id,
                                vmId: id
                            }, detailErr);
                            return { success: false, firewall };
                        })
                );
                
                const detailsResponses = await Promise.all(detailsPromises);
                
                const firewallsWithDetails = detailsResponses.map((response, index) => {
                    if (response.success) {
                        return Array.isArray(response.firewall) ? 
                            response.firewall[0] : response.firewall;
                    }
                    return response.firewall || response.attached_firewalls[index];
                });
                
                setAttachedFirewalls(firewallsWithDetails);
                
                logPerformance('fetch_attached_firewalls', duration, {
                    success: true,
                    vmId: id,
                    firewallCount: firewallsWithDetails.length
                });
                
                logInfo('Attached firewalls fetched successfully', {
                    vmId: id,
                    firewallCount: firewallsWithDetails.length,
                    duration: `${duration.toFixed(2)}ms`
                });
            } else {
                setAttachedFirewalls([]);
                
                logPerformance('fetch_attached_firewalls', duration, {
                    success: true,
                    vmId: id,
                    firewallCount: 0
                });
            }
        } else {
            const errorMessage = getSafeErrorMessage(response);
            setFirewallError(errorMessage);
            throw new Error(errorMessage);
        }
   } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        setIsLoadingFirewalls(false);
        isFetchingFirewalls.current = false;
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setFirewallError(err.error);
        logError('Network error in fetchAttachedFirewalls', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId: id
        }, err);
        setIsLoadingFirewalls(false);
        isFetchingFirewalls.current = false;
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setFirewallError(err.error);
        setIsLoadingFirewalls(false);
        isFetchingFirewalls.current = false;
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setFirewallError(ERROR_MESSAGES[err.code]);
        logError('Firewall fetch failed', {
            code: err.code,
            vmId: id,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        setIsLoadingFirewalls(false);
        isFetchingFirewalls.current = false;
        return;
    }
    
    // Fallback
    setFirewallError(err.error || 'An unexpected error occurred');
    
    logPerformance('fetch_attached_firewalls', duration, {
        success: false,
        error: err.error
    });
    
    logError('Attached firewalls fetch failed', {
        vmId: id,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);

    } finally {
        setIsLoadingFirewalls(false);
        isFetchingFirewalls.current = false;
    }
};
// Clear error callbacks for manual error dismissal
    const clearError = useCallback(() => {
        setError(null);
        setLastShownError('');
    }, []);

    const clearActionError = useCallback(() => {
        setActionError(null);
        setLastShownActionError('');
    }, []);

    const clearIpActionError = useCallback(() => {
        setIpActionError(null);
        setLastShownIpActionError('');
    }, []);

    const clearFirewallError = useCallback(() => {
        setFirewallError(null);
        setLastShownFirewallError('');
    }, []);

    const clearAddRuleError = useCallback(() => {
        setAddRuleError(null);
        setLastShownAddRuleError('');
    }, []);

 
    const refreshVmDetails = useCallback(async () => {
        try {
            logInfo('Refreshing VM details', { vmId });
            await fetchVmDetails();
        } catch (error) {
            logError('VM details refresh failed', { vmId }, error);
        }
    }, [vmId]);

    const refreshAttachedFirewalls = useCallback(async () => {
        try {
            logInfo('Refreshing attached firewalls', { vmId });
            await fetchAttachedFirewalls(vmId);
        } catch (error) {
            logError('Attached firewalls refresh failed', { vmId }, error);
        }
    }, [vmId]);

    // PATTERN 1, 3, 4: togglePublicIP with ALL patterns

const togglePublicIP = async () => {
    if (!vmDetails) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        logError('Toggle IP attempted with no VM details');
        return;
    }

    if (isTogglingIP) return;

    if (!canModifyPublicIP(vmDetails)) {
        const errorMsg = isAdvancedRegion(vmDetails) 
            ? 'Public IP management not available in advanced regions'
            : 'VM must be active to modify public IP';
        setIpActionError(errorMsg);
        logUserAction('toggle_ip_blocked', {
            vmId: vmDetails.instance_id,
            vmName: vmDetails.name,
            reason: errorMsg
        });
        return;
    }

    const ipId = vmDetails.floating_ip || vmDetails.instance_id;

    if (operationRefs.current.togglingIP.has(ipId)) {
        return;
    }
    
    const startTime = performance.now();
    const hasFloatingIP = Boolean(vmDetails.floating_ip);
    const action = hasFloatingIP ? 'detach' : 'attach';
    
    operationRefs.current.togglingIP.add(ipId);
    
    setIsTogglingIP(true);
    setIpActionError(null);
    setIpActionSuccess(null);
    
    setLockedVms(prev => new Set([...prev, vmDetails.instance_id]));
    setTimeout(() => {
        setLockedVms(prev => {
            const newLocked = new Set(prev);
            newLocked.delete(vmDetails.instance_id);
            return newLocked;
        });
    }, 3000);
    
    logUserAction(`toggle_ip_${action}_initiated`, {
        vmId: vmDetails.instance_id,
        vmName: vmDetails.name,
        currentIp: vmDetails.floating_ip || 'none'
    });
    
    logInfo(`Starting public IP ${action}`, {
        vmId: vmDetails.instance_id,
        vmName: vmDetails.name
    });
    
    try {
        const response = await api.toggleVMPublicIP(vmDetails.instance_id, action);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setIpActionSuccess(`Successfully ${hasFloatingIP ? 'removed' : 'added'} public IP`);

            setTimeout(() => {
                debouncedFetchVmDetails(200);
            }, 500);
            
            logPerformance(`toggle_ip_${action}`, duration, {
                success: true,
                vmId: vmDetails.instance_id,
                newIp: response.vm_details.floating_ip || 'removed'
            });
            
            logInfo(`Public IP ${action} successful`, {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                newIp: response.vm_details.floating_ip || 'removed',
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction(`toggle_ip_${action}_success`, {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || `Failed to ${action} public IP`;
            setIpActionError(errorMessage);
            throw new Error(errorMessage);
        }
    } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.togglingIP.delete(ipId);
        setIsTogglingIP(false);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setIpActionError(err.error);
        logError(`Network error in togglePublicIP (${action})`, { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId: vmDetails.instance_id
        }, err);
        operationRefs.current.togglingIP.delete(ipId);
        setIsTogglingIP(false);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setIpActionError(err.error);
        operationRefs.current.togglingIP.delete(ipId);
        setIsTogglingIP(false);
        return;
    }
    
    // VM not found
    if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
        notification.error('This VM no longer exists');
        operationRefs.current.togglingIP.delete(ipId);
        setIsTogglingIP(false);
        onBack();
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setIpActionError(ERROR_MESSAGES[err.code]);
        logError(`IP ${action} failed`, {
            code: err.code,
            vmId: vmDetails.instance_id,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.togglingIP.delete(ipId);
        setIsTogglingIP(false);
        return;
    }
    
    // Fallback
    setIpActionError(err.error || `Failed to ${action} public IP`);
    
    logPerformance(`toggle_ip_${action}`, duration, {
        success: false,
        error: err.error
    });
    
    logError(`Public IP ${action} failed`, {
        vmId: vmDetails.instance_id,
        vmName: vmDetails.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction(`toggle_ip_${action}_failed`, {
        vmId: vmDetails.instance_id,
        vmName: vmDetails.name,
        error: err.error
    });
} finally {
        operationRefs.current.togglingIP.delete(ipId);
        setIsTogglingIP(false);
    }
};

const handleAddRule = async () => {
    if (!vmDetails) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        logError('Add rule attempted with no VM details');
        return;
    }

    if (!canAddSecurityRule(vmDetails)) {
        const errorMsg = isAdvancedRegion(vmDetails)
            ? 'Security rules not available in advanced regions'
            : 'VM must be active to add security rules';
        setAddRuleError(errorMsg);
        logUserAction('add_rule_blocked', {
            vmId: vmDetails?.instance_id,
            vmName: vmDetails?.name,
            reason: errorMsg
        });
        return;
    }

    const ruleKey = `${vmDetails.instance_id}_${newRule.protocol}_${newRule.direction}`;

    if (operationRefs.current.addingRule.has(ruleKey)) {
        return;
    }

    const startTime = performance.now();
    
    operationRefs.current.addingRule.add(ruleKey);
    
    setAddRuleError(null);
    setIsAddingRule(true);
    
    logUserAction('add_security_rule_initiated', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        ruleDirection: newRule.direction,
        ruleProtocol: newRule.protocol
    });
    
    logInfo('Adding security rule', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        rule: newRule
    });
    
    try {
        const ruleData = { ...newRule };
        
        if (!useSpecificPorts || newRule.protocol === 'icmp') {
            delete ruleData.port_range_min;
            delete ruleData.port_range_max;
        } else {
            if (!ruleData.port_range_min || !ruleData.port_range_max) {
                throw new Error('Both minimum and maximum port range must be specified');
            }
            
            if (ruleData.port_range_min > ruleData.port_range_max) {
                throw new Error('Minimum port cannot be greater than maximum port');
            }
        }
        
        const response = await api.addVmSecurityRule(vmDetails.instance_id, ruleData);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setShowAddRuleModal(false);
            setRuleActionStatus({
                type: 'success',
                message: 'Security rule added successfully'
            });
            
            setNewRule({
                direction: 'ingress',
                protocol: 'tcp',
                ethertype: 'IPv4',
                remote_ip_prefix: '0.0.0.0/0',
                port_range_min: '',
                port_range_max: ''
            });
            setUseSpecificPorts(false);
            
            setTimeout(() => {
                debouncedFetchVmDetails(200);
            }, 500);
            
            setTimeout(() => {
                setRuleActionStatus(null);
            }, 5000);
            
            logPerformance('add_security_rule', duration, {
                success: true,
                vmId: vmDetails.instance_id,
                ruleProtocol: newRule.protocol
            });
            
            logInfo('Security rule added successfully', {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('add_security_rule_success', {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to add security rule';
            const errorCode = response.code || '';
            
            if (errorCode === 'SECURITY_RULE_ALREADY_EXISTS') {
                setAddRuleError('This security rule already exists. Please check your existing rules.');
            } else {
                setAddRuleError(errorMessage);
            }
            
            logError('Security rule add failed', {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                errorCode,
                errorMessage
            });
        }
    } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Local validation errors (thrown before API call)
    if (err.message && !err.code && (
        err.message.includes('port range must be specified') ||
        err.message.includes('Minimum port cannot be greater than maximum port')
    )) {
        setAddRuleError(err.message);
        logError('Add rule validation error', {
            vmId: vmDetails.instance_id,
            error: err.message,
            duration: `${duration.toFixed(2)}ms`
        });
        operationRefs.current.addingRule.delete(ruleKey);
        setIsAddingRule(false);
        return;
    }
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.addingRule.delete(ruleKey);
        setIsAddingRule(false);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setAddRuleError(err.error);
        logError('Network error in handleAddRule', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId: vmDetails.instance_id
        }, err);
        operationRefs.current.addingRule.delete(ruleKey);
        setIsAddingRule(false);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setAddRuleError(err.error);
        operationRefs.current.addingRule.delete(ruleKey);
        setIsAddingRule(false);
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setAddRuleError(ERROR_MESSAGES[err.code]);
        logError('Add rule failed', {
            code: err.code,
            vmId: vmDetails.instance_id,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.addingRule.delete(ruleKey);
        setIsAddingRule(false);
        return;
    }
    
    // Fallback
    setAddRuleError(err.error || 'An error occurred while adding the security rule');
    
    logPerformance('add_security_rule', duration, {
        success: false,
        error: err.error
    });
    
    logError('Security rule add error', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('add_security_rule_failed', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        error: err.error
    });
} finally {
        operationRefs.current.addingRule.delete(ruleKey);
        setIsAddingRule(false);
    }
};

const handleDeleteRule = async () => {
    if (!vmDetails || !selectedRuleForDelete) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        setSelectedRuleForDelete(null);
        logError('Delete rule attempted with invalid data', {
            hasVmDetails: !!vmDetails,
            ruleId: selectedRuleForDelete?.id
        });
        return;
    }

    if (!canAddSecurityRule(vmDetails)) {
        setRuleActionStatus({
            type: 'error',
            message: isAdvancedRegion(vmDetails)
                ? 'Security rules not available in advanced regions'
                : 'VM must be active to delete security rules'
        });
        logUserAction('delete_rule_blocked', {
            vmId: vmDetails?.instance_id,
            vmName: vmDetails?.name,
            ruleId: selectedRuleForDelete?.id
        });
        setSelectedRuleForDelete(null);
        return;
    }

    const ruleId = selectedRuleForDelete.id;

    if (operationRefs.current.deletingRule.has(ruleId)) {
        return;
    }

    const startTime = performance.now();
    
    operationRefs.current.deletingRule.add(ruleId);
    
    setIsDeletingRule(ruleId);
    setRuleActionStatus(null);
    
    logUserAction('delete_security_rule_initiated', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        ruleId
    });
    
    logInfo('Deleting security rule', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        ruleId
    });
    
    try {
        const response = await api.removeVmSecurityRule(vmDetails.instance_id, ruleId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setRuleActionStatus({
                type: 'success',
                message: response.message || 'Security rule deleted successfully'
            });
            
            setTimeout(() => {
                fetchVmDetails();
            }, 500);
            
            setTimeout(() => {
                setRuleActionStatus(null);
            }, 5000);
            
            logPerformance('delete_security_rule', duration, {
                success: true,
                vmId: vmDetails.instance_id,
                ruleId
            });
            
            logInfo('Security rule deleted successfully', {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                ruleId,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('delete_security_rule_success', {
                vmId: vmDetails.instance_id,
                vmName: vmDetails.name,
                ruleId,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to delete security rule';
            setRuleActionStatus({
                type: 'error',
                message: errorMessage
            });
            throw new Error(errorMessage);
        }
    } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.deletingRule.delete(ruleId);
        setIsDeletingRule(null);
        setSelectedRuleForDelete(null);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setRuleActionStatus({
            type: 'error',
            message: err.error
        });
        logError('Network error in handleDeleteRule', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId: vmDetails.instance_id,
            ruleId
        }, err);
        operationRefs.current.deletingRule.delete(ruleId);
        setIsDeletingRule(null);
        setSelectedRuleForDelete(null);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setRuleActionStatus({
            type: 'error',
            message: err.error
        });
        operationRefs.current.deletingRule.delete(ruleId);
        setIsDeletingRule(null);
        setSelectedRuleForDelete(null);
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setRuleActionStatus({
            type: 'error',
            message: ERROR_MESSAGES[err.code]
        });
        logError('Delete rule failed', {
            code: err.code,
            vmId: vmDetails.instance_id,
            ruleId,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.deletingRule.delete(ruleId);
        setIsDeletingRule(null);
        setSelectedRuleForDelete(null);
        return;
    }
    
    // Fallback
    setRuleActionStatus({
        type: 'error',
        message: err.error || 'An error occurred while deleting the security rule'
    });
    
    logPerformance('delete_security_rule', duration, {
        success: false,
        error: err.error
    });
    
    logError('Security rule delete error', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        ruleId,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('delete_security_rule_failed', {
        vmId: vmDetails?.instance_id,
        vmName: vmDetails?.name,
        ruleId,
        error: err.error
    });
} finally {
        operationRefs.current.deletingRule.delete(ruleId);
        setIsDeletingRule(null);
        setSelectedRuleForDelete(null);
    }
};
    // Modal Click Handlers with logging
    const handleStartClick = useCallback(() => {
        // PATTERN 6: Defensive validation
        if (!vmDetails) {
            notification.error(ERROR_MESSAGES['INVALID_DATA']);
            logError('Start click attempted with no VM details');
            return;
        }

        logUserAction('start_vm_modal_opened', {
            vmId: vmDetails?.instance_id,
            vmName: vmDetails?.name,
            vmStatus: vmDetails?.status
        });
        setSelectedVmForStart(vmDetails);
    }, [vmDetails, notification]);

    const handleStopClick = useCallback(() => {
        // PATTERN 6: Defensive validation
        if (!vmDetails) {
            notification.error(ERROR_MESSAGES['INVALID_DATA']);
            logError('Stop click attempted with no VM details');
            return;
        }

        logUserAction('stop_vm_modal_opened', {
            vmId: vmDetails?.instance_id,
            vmName: vmDetails?.name,
            vmStatus: vmDetails?.status
        });
        setSelectedVmForStop(vmDetails);
    }, [vmDetails, notification]);

    const handleRebootClick = useCallback(() => {
        // PATTERN 6: Defensive validation
        if (!vmDetails) {
            notification.error(ERROR_MESSAGES['INVALID_DATA']);
            logError('Reboot click attempted with no VM details');
            return;
        }

        // PATTERN 2: Use validation helper
        if (!canRebootVM(vmDetails)) {
            const errorMsg = 'VM reboot not available in advanced regions';
            setActionError(errorMsg);
            logUserAction('reboot_vm_blocked_advanced_region', {
                vmId: vmDetails?.instance_id,
                vmName: vmDetails?.name,
                region: vmDetails?.region
            });
            return;
        }
        
        logUserAction('reboot_vm_modal_opened', {
            vmId: vmDetails?.instance_id,
            vmName: vmDetails?.name,
            vmStatus: vmDetails?.status
        });
        setSelectedVmForReboot(vmDetails);
    }, [vmDetails, notification]);

    const handleRestoreClick = useCallback(() => {
        // PATTERN 6: Defensive validation
        if (!vmDetails) {
            notification.error(ERROR_MESSAGES['INVALID_DATA']);
            logError('Restore click attempted with no VM details');
            return;
        }

        // PATTERN 2: Use validation helper
        if (!canRestoreVM(vmDetails)) {
            const errorMsg = 'VM restore not available in advanced regions';
            setActionError(errorMsg);
            logUserAction('restore_vm_blocked_advanced_region', {
                vmId: vmDetails?.instance_id,
                vmName: vmDetails?.name,
                region: vmDetails?.region
            });
            return;
        }
        
        logUserAction('restore_vm_modal_opened', {
            vmId: vmDetails?.instance_id,
            vmName: vmDetails?.name,
            vmStatus: vmDetails?.status
        });
        setSelectedVmForRestore(vmDetails);
    }, [vmDetails, notification]);


const handleStartVM = async () => {
    if (!selectedVmForStart) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        setSelectedVmForStart(null);
        logError('Start VM attempted with no VM selected');
        return;
    }

    const vmId = selectedVmForStart.instance_id;

    if (operationRefs.current.starting.has(vmId) || loadingStates.starting.has(vmId)) {
        return;
    }

    if (!canStartVM(selectedVmForStart)) {
        const errorMsg = `Cannot start - VM is ${selectedVmForStart.status}`;
        setActionError(errorMsg);
        setSelectedVmForStart(null);
        logError('Start VM blocked - invalid status', {
            vmId,
            status: selectedVmForStart.status
        });
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.starting.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        starting: new Set([...prev.starting, vmId])
    }));
    setActionError(null);
    setActionSuccess(null);
    
    cacheVMOperation(vmId, 'STARTING');
    
    setVmDetails(prev => ({
        ...prev,
        status: 'STARTING'
    }));
    
    setLockedVms(prev => new Set([...prev, vmId]));
    setTimeout(() => {
        setLockedVms(prev => {
            const newLocked = new Set(prev);
            newLocked.delete(vmId);
            return newLocked;
        });
    }, 3000);
    
    logUserAction('vm_start_confirmed', {
        vmId,
        vmName: selectedVmForStart.name
    });
    
    logInfo('Starting VM', {
        vmId,
        vmName: selectedVmForStart.name
    });
    
    try {
        const response = await api.startVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionSuccess('VM start initiated successfully');
            
            setTimeout(() => {
                debouncedFetchVmDetails(200);
            }, 500);
            
            logPerformance('start_vm', duration, {
                success: true,
                vmId
            });
            
            logInfo('VM start successful', {
                vmId,
                vmName: selectedVmForStart.name,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_start_success', {
                vmId,
                vmName: selectedVmForStart.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to start VM';
            setActionError(errorMessage);
            throw new Error(errorMessage);
        }
   } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    clearVMOperationCache();
    
    setVmDetails(prev => ({
        ...prev,
        status: selectedVmForStart.status
    }));
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setActionError(err.error);
        logError('Network error in handleStartVM', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId
        }, err);
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setActionError(err.error);
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
        return;
    }
    
    // VM not found
    if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
        notification.error('This VM no longer exists');
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
        onBack();
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setActionError(ERROR_MESSAGES[err.code]);
        logError('Start VM failed', {
            code: err.code,
            vmId,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
        return;
    }
    
    // Fallback
    setActionError(err.error || 'Failed to start VM');
    
    logPerformance('start_vm', duration, {
        success: false,
        error: err.error
    });
    
    logError('VM start failed', {
        vmId,
        vmName: selectedVmForStart.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('vm_start_failed', {
        vmId,
        vmName: selectedVmForStart.name,
        error: err.error
    });
} finally {
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
    }
};
const handleStopVM = async () => {
    if (!selectedVmForStop) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        setSelectedVmForStop(null);
        logError('Stop VM attempted with no VM selected');
        return;
    }

    const vmId = selectedVmForStop.instance_id;

    if (operationRefs.current.stopping.has(vmId) || loadingStates.stopping.has(vmId)) {
        return;
    }

    if (!canStopVM(selectedVmForStop)) {
        const errorMsg = `Cannot stop - VM is ${selectedVmForStop.status}`;
        setActionError(errorMsg);
        setSelectedVmForStop(null);
        logError('Stop VM blocked - invalid status', {
            vmId,
            status: selectedVmForStop.status
        });
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.stopping.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        stopping: new Set([...prev.stopping, vmId])
    }));
    setActionError(null);
    setActionSuccess(null);
    
    cacheVMOperation(vmId, 'STOPPING');
    
    setVmDetails(prev => ({
        ...prev,
        status: 'STOPPING'
    }));
    
    setLockedVms(prev => new Set([...prev, vmId]));
    setTimeout(() => {
        setLockedVms(prev => {
            const newLocked = new Set(prev);
            newLocked.delete(vmId);
            return newLocked;
        });
    }, 3000);
    
    logUserAction('vm_stop_confirmed', {
        vmId,
        vmName: selectedVmForStop.name
    });
    
    logInfo('Stopping VM', {
        vmId,
        vmName: selectedVmForStop.name
    });
    
    try {
        const response = await api.stopVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionSuccess('VM stop initiated successfully');
            
            setTimeout(() => {
                debouncedFetchVmDetails(200);
            }, 500);
            
            logPerformance('stop_vm', duration, {
                success: true,
                vmId
            });
            
            logInfo('VM stop successful', {
                vmId,
                vmName: selectedVmForStop.name,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_stop_success', {
                vmId,
                vmName: selectedVmForStop.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to stop VM';
            setActionError(errorMessage);
            throw new Error(errorMessage);
        }
   } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    clearVMOperationCache();
    
    setVmDetails(prev => ({
        ...prev,
        status: selectedVmForStop.status
    }));
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setActionError(err.error);
        logError('Network error in handleStopVM', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId
        }, err);
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setActionError(err.error);
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
        return;
    }
    
    // VM not found
    if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
        notification.error('This VM no longer exists');
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
        onBack();
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setActionError(ERROR_MESSAGES[err.code]);
        logError('Stop VM failed', {
            code: err.code,
            vmId,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
        return;
    }
    
    // Fallback
    setActionError(err.error || 'Failed to stop VM');
    
    logPerformance('stop_vm', duration, {
        success: false,
        error: err.error
    });
    
    logError('VM stop failed', {
        vmId,
        vmName: selectedVmForStop.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('vm_stop_failed', {
        vmId,
        vmName: selectedVmForStop.name,
        error: err.error
    });
} finally {
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
    }
};

const handleRebootVM = async () => {
    if (!selectedVmForReboot) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        setSelectedVmForReboot(null);
        logError('Reboot VM attempted with no VM selected');
        return;
    }

    const vmId = selectedVmForReboot.instance_id;

    if (operationRefs.current.rebooting.has(vmId) || loadingStates.rebooting.has(vmId)) {
        return;
    }

    if (!canRebootVM(selectedVmForReboot)) {
        setActionError('VM reboot not available in advanced regions');
        setSelectedVmForReboot(null);
        logError('Reboot VM blocked', {
            vmId,
            region: selectedVmForReboot.region
        });
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.rebooting.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        rebooting: new Set([...prev.rebooting, vmId])
    }));
    setActionError(null);
    setActionSuccess(null);
    
    cacheVMOperation(vmId, 'REBOOTING');
    
    setVmDetails(prev => ({
        ...prev,
        status: 'REBOOTING'
    }));
    
    setLockedVms(prev => new Set([...prev, vmId]));
    setTimeout(() => {
        setLockedVms(prev => {
            const newLocked = new Set(prev);
            newLocked.delete(vmId);
            return newLocked;
        });
    }, 3000);
    
    logUserAction('vm_reboot_confirmed', {
        vmId,
        vmName: selectedVmForReboot.name
    });
    
    logInfo('Rebooting VM', {
        vmId,
        vmName: selectedVmForReboot.name
    });
    
    try {
        const response = await api.rebootVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionSuccess('VM reboot initiated successfully');
            
            setTimeout(() => {
                debouncedFetchVmDetails(200);
            }, 500);
            
            logPerformance('reboot_vm', duration, {
                success: true,
                vmId
            });
            
            logInfo('VM reboot successful', {
                vmId,
                vmName: selectedVmForReboot.name,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_reboot_success', {
                vmId,
                vmName: selectedVmForReboot.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to reboot VM';
            setActionError(errorMessage);
            throw new Error(errorMessage);
        }
    } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    clearVMOperationCache();
    
    setVmDetails(prev => ({
        ...prev,
        status: selectedVmForReboot.status
    }));
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setActionError(err.error);
        logError('Network error in handleRebootVM', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId
        }, err);
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setActionError(err.error);
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
        return;
    }
    
    // VM not found
    if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
        notification.error('This VM no longer exists');
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
        onBack();
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setActionError(ERROR_MESSAGES[err.code]);
        logError('Reboot VM failed', {
            code: err.code,
            vmId,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
        return;
    }
    
    // Fallback
    setActionError(err.error || 'Failed to reboot VM');
    
    logPerformance('reboot_vm', duration, {
        success: false,
        error: err.error
    });
    
    logError('VM reboot failed', {
        vmId,
        vmName: selectedVmForReboot.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('vm_reboot_failed', {
        vmId,
        vmName: selectedVmForReboot.name,
        error: err.error
    });
}finally {
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
    }
};

const handleRestoreVM = async () => {
    if (!selectedVmForRestore) {
        notification.error(ERROR_MESSAGES['INVALID_DATA']);
        setSelectedVmForRestore(null);
        logError('Restore VM attempted with no VM selected');
        return;
    }

    const vmId = selectedVmForRestore.instance_id;

    if (operationRefs.current.restoring.has(vmId) || loadingStates.restoring.has(vmId)) {
        return;
    }

    if (!canRestoreVM(selectedVmForRestore)) {
        setActionError('VM restore not available in advanced regions');
        setSelectedVmForRestore(null);
        logError('Restore VM blocked', {
            vmId,
            region: selectedVmForRestore.region
        });
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.restoring.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        restoring: new Set([...prev.restoring, vmId])
    }));
    setActionError(null);
    setActionSuccess(null);
    
    cacheVMOperation(vmId, 'RESTORING');
    
    setVmDetails(prev => ({
        ...prev,
        status: 'RESTORING'
    }));
    
    setLockedVms(prev => new Set([...prev, vmId]));
    setTimeout(() => {
        setLockedVms(prev => {
            const newLocked = new Set(prev);
            newLocked.delete(vmId);
            return newLocked;
        });
    }, 3000);
    
    logUserAction('vm_restore_confirmed', {
        vmId,
        vmName: selectedVmForRestore.name
    });
    
    logInfo('Restoring VM', {
        vmId,
        vmName: selectedVmForRestore.name
    });
    
    try {
        const response = await api.restoreHibernatedVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionSuccess('VM restore initiated successfully');
            
            setTimeout(() => {
                debouncedFetchVmDetails(200);
            }, 500);
            
            logPerformance('restore_vm', duration, {
                success: true,
                vmId
            });
            
            logInfo('VM restore successful', {
                vmId,
                vmName: selectedVmForRestore.name,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_restore_success', {
                vmId,
                vmName: selectedVmForRestore.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to restore VM';
            setActionError(errorMessage);
            throw new Error(errorMessage);
        }
    } catch (err) {
    if (err.name === 'AbortError') {
        return;
    }
    
    clearVMOperationCache();
    
    setVmDetails(prev => ({
        ...prev,
        status: selectedVmForRestore.status
    }));
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        handleAuthError();
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
        return;
    }
    
    // Network error
    if (!err.status && err.code) {
        setActionError(err.error);
        logError('Network error in handleRestoreVM', { 
            code: err.code,
            online: navigator.onLine,
            duration: `${duration.toFixed(2)}ms`,
            vmId
        }, err);
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setActionError(err.error);
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
        return;
    }
    
    // VM not found
    if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
        notification.error('This VM no longer exists');
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
        onBack();
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setActionError(ERROR_MESSAGES[err.code]);
        logError('Restore VM failed', {
            code: err.code,
            vmId,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
        return;
    }
    
    // Fallback
    setActionError(err.error || 'Failed to restore VM');
    
    logPerformance('restore_vm', duration, {
        success: false,
        error: err.error
    });
    
    logError('VM restore failed', {
        vmId,
        vmName: selectedVmForRestore.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('vm_restore_failed', {
        vmId,
        vmName: selectedVmForRestore.name,
        error: err.error
    });
} finally {
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
    }
};    // PATTERN 2: Use validation helpers for button enable/disable logic
    const isVmActive = canStopVM(vmDetails);
    const isVmStopped = canStartVM(vmDetails);
    const isVmHibernated = canRestoreVM(vmDetails);
    const isAdvanced = isAdvancedRegion(vmDetails);
    const canStart = canStartVM(vmDetails);
    const canStop = canStopVM(vmDetails);
    const canReboot = canRebootVM(vmDetails);
    const canRestore = canRestoreVM(vmDetails);
    const canHibernate = canHibernateVM(vmDetails);
    const canDelete = canDeleteVM(vmDetails);
  
  const RefreshIndicator = () => (
        <div className="fixed top-4 right-4 z-50 bg-[#121a15] border border-[#1a2a1a] rounded-lg px-4 py-2 flex items-center gap-2 shadow-lg">
            <div className="w-4 h-4 border-2 border-[#e0eacc] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-[#e0eacc]">Refreshing...</span>
        </div>
    );
    // Determine loading display mode
    const showFullSkeleton = isLoading && (!initialDataLoaded || isManualRefresh);
    const showRefreshIndicator = isRefreshing && initialDataLoaded && !isManualRefresh;
    const getUsageHours = () => {
        switch(usagePeriod) {
            case 'hour': return usageAmount;
            case 'day': return usageAmount * 24;
            case 'month': return usageAmount * 730;
            case 'year': return usageAmount * 8760;
            default: return usageAmount;
        }
    };

    const calculateTotalCost = () => {
        return calculateVmCost.hourly * getUsageHours();
    };

  const needsCustomPricing = () => {
    return getUsageHours() >= 730 || usagePeriod === 'month' || usagePeriod === 'year';
};const getMaxUsageValue = () => {
    switch(usagePeriod) {
        case 'hour': return 43800;  // 5 years in hours
        case 'day': return 1825;     // 5 years in days
        case 'month': return 60;
        case 'year': return 5;
        default: return 99999;
    }
};
const handleInputChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue === '') {
        setInputValue('');
        setIsInputValid(false);
        return;
    }
    
    setInputValue(numericValue);
    
    const parsed = parseInt(numericValue);
    const maxVal = getMaxUsageValue();
    
    if (parsed >= 1 && parsed <= maxVal) {
        setIsInputValid(true);
        setUsageAmount(parsed);
    } else {
        setIsInputValid(false);
    }
};

const handleInputKeyDown = (e) => {
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(e.keyCode) !== -1 ||
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
        
        if (e.keyCode === 38) { // Arrow up
            e.preventDefault();
            const maxVal = getMaxUsageValue();
            const increment = usagePeriod === 'hour' ? 100 : usagePeriod === 'day' ? 10 : 1;
            const newValue = Math.min(usageAmount + increment, maxVal);
            setUsageAmount(newValue);
            setInputValue(String(newValue));
            setIsInputValid(true);
        } else if (e.keyCode === 40) { // Arrow down
            e.preventDefault();
            const increment = usagePeriod === 'hour' ? 100 : usagePeriod === 'day' ? 10 : 1;
            const newValue = Math.max(usageAmount - increment, 1);
            setUsageAmount(newValue);
            setInputValue(String(newValue));
            setIsInputValid(true);
        } else if (e.keyCode === 13 || e.keyCode === 9) { // Enter or Tab
            e.target.blur();
        }
        
        return;
    }
    
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
    }
};

const handleInputFocus = (e) => {
    setInputFocused(true);
    setInputValue(String(usageAmount));
};

const handleInputBlur = (e) => {
    setInputFocused(false);
    const value = e.target.value.replace(/\D/g, '');
    
    if (value === '') {
        setUsageAmount(1);
        setInputValue('1');
        setIsInputValid(true);
        return;
    }
    
    const parsed = parseInt(value);
    const maxVal = getMaxUsageValue();
    let finalValue = parsed;
    
    if (parsed < 1) {
        finalValue = 1;
    } else if (parsed > maxVal) {
        finalValue = maxVal;
    }
    
    setUsageAmount(finalValue);
    setInputValue(String(finalValue));
    setIsInputValid(true);
};
const handlePeriodChange = (newPeriod) => {
    setUsagePeriod(newPeriod);
    let maxVal;
    switch(newPeriod) {
        case 'hour': maxVal = 43800; break;  // 5 years
        case 'day': maxVal = 1825; break;     // 5 years
        case 'month': maxVal = 60; break;
        case 'year': maxVal = 5; break;
        default: maxVal = 99999;
    }
    if (usageAmount > maxVal) {
        setUsageAmount(maxVal);
    }
};

    const vmCost = calculateVmCost;

  if (showFullSkeleton) {
    return (
        <div className="min-h-screen bg-black text-white">
            <style>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                @keyframes pulse-glow {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 0.8; }
                }
                .shimmer-bg {
                    background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.5s ease-in-out infinite;
                }
                .pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
            `}</style>
            
            <div className={`max-w-7xl mx-auto ${isMobile ? 'px-4 py-6' : 'px-4 py-10'}`}>
                {/* Header Skeleton */}
                <div className="mb-8">
                    <div className={`flex ${isMobile ? 'flex-col gap-4' : 'flex-row items-center justify-between'}`}>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl shimmer-bg"></div>
                            <div>
                                <div className={`h-7 shimmer-bg rounded-lg mb-2 ${isMobile ? 'w-48' : 'w-64'}`}></div>
                                <div className={`h-4 shimmer-bg rounded-lg ${isMobile ? 'w-64' : 'w-96'}`}></div>
                            </div>
                        </div>
                        <div className={`h-10 shimmer-bg rounded-md ${isMobile ? 'w-full' : 'w-40'}`}></div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-10'}`}>
                    {/* Left Column */}
                    <div className={`space-y-6 ${isMobile ? '' : 'lg:col-span-7'}`}>
                        {/* Hardware Specs Card */}
                        <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[#1a2a1a] rounded-xl overflow-hidden">
                            <div className="border-b border-[#1c2b22] px-6 py-5">
                                <div className="h-5 w-48 shimmer-bg rounded"></div>
                            </div>
                            <div className="p-6">
                                <div className={`grid gap-4 mb-6 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
                                    {[0, 1, 2, 3].map((i) => (
                                        <div key={i} className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-4">
                                            <div className="h-3 w-16 shimmer-bg rounded mb-3"></div>
                                            <div className="h-6 w-12 shimmer-bg rounded"></div>
                                        </div>
                                    ))}
                                </div>
                                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                    <div className="flex gap-2">
                                        <div className="h-4 w-16 shimmer-bg rounded"></div>
                                        <div className="h-4 w-24 shimmer-bg rounded"></div>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="h-4 w-14 shimmer-bg rounded"></div>
                                        <div className="h-4 w-32 shimmer-bg rounded"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Network Card */}
                        <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[#1a2a1a] rounded-xl overflow-hidden">
                            <div className="border-b border-[#1c2b22] px-6 py-5 flex items-center justify-between">
                                <div className="h-5 w-44 shimmer-bg rounded"></div>
                                <div className="h-9 w-24 shimmer-bg rounded-md"></div>
                            </div>
                            <div className="p-6">
                                <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                    {[0, 1].map((i) => (
                                        <div key={i} className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="h-4 w-28 shimmer-bg rounded"></div>
                                                <div className="h-5 w-16 shimmer-bg rounded"></div>
                                            </div>
                                            <div className="h-6 w-36 shimmer-bg rounded mb-2"></div>
                                            <div className="h-3 w-full shimmer-bg rounded"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Security Rules Card */}
                        <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[#1a2a1a] rounded-xl overflow-hidden">
                            <div className="border-b border-[#1c2b22] px-6 py-5 flex items-center justify-between">
                                <div className="h-5 w-32 shimmer-bg rounded"></div>
                                <div className="h-9 w-20 shimmer-bg rounded-md"></div>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className={`flex items-center justify-between p-4 bg-[#0a0c0a]/50 rounded-lg border border-[#1a2a1a]/20 ${isMobile ? 'flex-col gap-3' : ''}`}>
                                            <div className={`flex gap-4 ${isMobile ? 'flex-wrap w-full' : 'gap-8'}`}>
                                                <div className="h-4 w-12 shimmer-bg rounded"></div>
                                                <div className="h-4 w-16 shimmer-bg rounded"></div>
                                                <div className="h-4 w-24 shimmer-bg rounded"></div>
                                                <div className="h-4 w-16 shimmer-bg rounded"></div>
                                            </div>
                                            <div className="h-4 w-14 shimmer-bg rounded"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className={`space-y-6 ${isMobile ? '' : 'lg:col-span-3'}`}>
                        {/* VM Status Card */}
                        <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[#1a2a1a] rounded-xl overflow-hidden">
                            <div className="border-b border-[#1c2b22] px-6 py-5">
                                <div className="h-5 w-24 shimmer-bg rounded"></div>
                            </div>
                            <div className="p-6">
                                <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5 mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="h-4 w-24 shimmer-bg rounded"></div>
                                        <div className="h-4 w-20 shimmer-bg rounded"></div>
                                    </div>
                                    <div className="h-3 w-full shimmer-bg rounded"></div>
                                </div>
                                <div className="space-y-3">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="h-12 shimmer-bg rounded-lg"></div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Cost Calculator Card */}
                        <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[#1a2a1a] rounded-xl overflow-hidden">
                            <div className="border-b border-[#1c2b22] px-6 py-5">
                                <div className="h-5 w-32 shimmer-bg rounded"></div>
                            </div>
                            <div className="p-6">
                                <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5 space-y-4">
                                    <div className="flex justify-between">
                                        <div className="h-4 w-24 shimmer-bg rounded"></div>
                                        <div className="h-4 w-20 shimmer-bg rounded"></div>
                                    </div>
                                    <div className="h-10 shimmer-bg rounded"></div>
                                    <div className="h-10 shimmer-bg rounded"></div>
                                    <div className="pt-3 border-t border-[#1a2a1a]/30 space-y-2">
                                        <div className="flex justify-between">
                                            <div className="h-3 w-20 shimmer-bg rounded"></div>
                                            <div className="h-4 w-16 shimmer-bg rounded"></div>
                                        </div>
                                        <div className="flex justify-between">
                                            <div className="h-3 w-20 shimmer-bg rounded"></div>
                                            <div className="h-4 w-16 shimmer-bg rounded"></div>
                                        </div>
                                    </div>
                                    <div className="pt-3 border-t border-[#1a2a1a]/30">
                                        <div className="flex justify-between items-center">
                                            <div className="h-4 w-12 shimmer-bg rounded"></div>
                                            <div className="h-7 w-24 shimmer-bg rounded"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

    if (error || !vmDetails) {
        return (
            <ResponsiveWrapper
                error={error || 'VM details not found'}
            >
                <div className={`bg-[#060d18] rounded-xl border border-[#0f1e32] shadow-lg ${isMobile ? 'p-4' : 'p-8'}`}>
                    <h2 className={`font-bold text-white mb-4 ${isMobile ? 'text-xl' : 'text-2xl'}`}>Error</h2>
                    <p className={`text-gray-300 mb-6 ${isMobile ? 'text-sm' : 'text-base'}`}>{sanitizeText(error || 'VM details not found')}</p>
                    <button 
                        onClick={onBack} 
                        className={`bg-[#e0eacc] text-[#040911] font-medium rounded-md hover:bg-[#d0dab9] transition-colors ${
                            isMobile ? 'px-4 py-3 text-sm w-full' : 'px-6 py-3'
                        }`}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </ResponsiveWrapper>
        );
    }

return (
    <ResponsiveWrapper>
        {/* Refresh Indicator for Auto/Background Refreshes */}
        {showRefreshIndicator && <RefreshIndicator />}
        
        <style jsx>{`
            /* Global custom scrollbar for all scrollable sections */
            .custom-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: #555555 #2a2a2a;
            }
            .custom-scrollbar::-webkit-scrollbar {
                width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
                background: #2a2a2a;
                border-radius: 3px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
                background: #555555;
                border-radius: 3px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: #666666;
            }
                /* Subtle pulsing status for assigned public IP */
                .status-assigned-pulse {
                    background: rgba(202, 217, 238, 0.1) !important;
                    color: #cad9ee !important;
                    border: 1px solid rgba(202, 217, 238, 0.4) !important;
                    animation: pulse-assigned 3s infinite;
                }
                
                /* Subtle pulsing animation for assigned status */
                @keyframes pulse-assigned {
                    0% {
                        background: rgba(202, 217, 238, 0.1);
                        border-color: rgba(202, 217, 238, 0.4);
                        box-shadow: 0 0 0 0 rgba(202, 217, 238, 0.2);
                    }
                    50% {
                        background: rgba(202, 217, 238, 0.15);
                        border-color: rgba(202, 217, 238, 0.5);
                        box-shadow: 0 0 0 2px rgba(202, 217, 238, 0.1);
                    }
                    100% {
                        background: rgba(202, 217, 238, 0.1);
                        border-color: rgba(202, 217, 238, 0.4);
                        box-shadow: 0 0 0 0 rgba(202, 217, 238, 0);
                    }
                }
            `}</style>  
            {!isMobile && !isTablet ? (
                // Desktop layout
                <div className="min-h-screen bg-[#000000] text-white">
                    <div className="max-w-7xl mx-auto px-4 py-10">
                        {/* Header Section */}
                        <div className="mb-8">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <h1 className="text-3xl font-bold mb-2 flex items-center">
                                        <svg className="mr-3" width="32" height="32" viewBox="0 0 24 24" fill="none">
                                            <rect x="2" y="4" width="20" height="4" rx="1" fill="#cad9e3"/>
                                            <rect x="2" y="10" width="20" height="4" rx="1" fill="#cad9e3"/>
                                            <rect x="2" y="16" width="20" height="4" rx="1" fill="#cad9e3"/>
                                            <circle cx="6" cy="6" r="0.5" fill="#121a15"/>
                                            <circle cx="8" cy="6" r="0.5" fill="#121a15"/>
                                            <circle cx="6" cy="12" r="0.5" fill="#121a15"/>
                                            <circle cx="8" cy="12" r="0.5" fill="#121a15"/>
                                            <circle cx="6" cy="18" r="0.5" fill="#121a15"/>
                                            <circle cx="8" cy="18" r="0.5" fill="#121a15"/>
                                        </svg>
                                        {truncateText(sanitizeText(vmDetails.name), 80)}
                                    </h1>
                                    <p className="text-gray-400 text-sm">
                                        VM ID: {sanitizeText(vmDetails.instance_id || 'N/A')} | Created: {formatDate(vmDetails.created_at)}
                                    </p>
                                </div>
                                
                                <div className="flex flex-wrap gap-3 mt-2 md:mt-0">
                                    <button 
                                        className="px-4 py-2 bg-[#0a0c0a] text-white border border-[rgba(202,217,227,0.3)] rounded-md font-medium hover:bg-[#121a15] transition-colors"
                                        onClick={onBack}
                                    >
                                        Back to Dashboard
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
                            <div className="lg:col-span-7 space-y-6">
                                {/* Hardware Specifications */}
                                <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.15)] rounded-xl shadow-xl p-4">
                                    <div className="border-b border-[#1c2b22] px-6 py-5">
                                        <h3 className="text-lg font-light text-[#e0eacc]">Hardware Specifications</h3>
                                    </div>
                                    
                                    <div className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-4 text-center">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">GPU</div>
        <div className="text-lg font-light text-white">
            {vmDetails.specs?.gpu_count > 0 
                ? `${vmDetails.specs?.gpu_count}× ${vmDetails.specs?.gpu_type}`
                : 'None'}
        </div>
    </div>
    
    <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-4 text-center">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Memory</div>
        <div className="text-lg font-light text-white">{vmDetails.specs?.ram || 0} GB</div>
    </div>
    
    <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-4 text-center">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">CPU Cores</div>
        <div className="text-lg font-light text-white">{vmDetails.specs?.cpu || 0}</div>
    </div>
    
    <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-4 text-center">
        {isAdvancedRegion(vmDetails) ? (
            <>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">GPU Memory</div>
                <div className="text-lg font-light text-white">
                    {(() => {
                        const gpuString = vmDetails.specs?.gpu_type || '';
                        const memoryMatch = gpuString.match(/(\d+)GB/);
                        return memoryMatch ? `${memoryMatch[1]} GB` : 'N/A';
                    })()}
                </div>
            </>
        ) : (
            <>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Storage</div>
                <div className="text-lg font-light text-white">
                    {vmDetails.volume_attachments?.reduce((sum, vol) => sum + (vol.size || 0), 0) || 0} GB
                </div>
            </>
        )}
    </div>
</div>
                                        
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <span className="text-gray-400">Region:</span>
                                                <span className="text-white ml-2">{sanitizeText(vmDetails.region || 'Unknown')}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-400">Image:</span>
                                                <span className="text-white ml-2">{sanitizeText(vmDetails.image?.name || 'Unknown')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Network & Connectivity */}
                                <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.15)] rounded-xl shadow-xl p-4">
                                    <div className="border-b border-[#1c2b22] px-6 py-5 flex items-center justify-between">
                                        <h3 className="text-lg font-light text-[#e0eacc]">Network & Connectivity</h3>
                                        {!isAdvancedRegion(vmDetails) && (
                                            <button 
                                                className={`rounded-md font-medium transition-colors ${
                                                    !isVmActive 
                                                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                                                        : vmDetails.floating_ip 
                                                            ? 'bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555]'
                                                            : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                                                } px-4 py-2`}
                                                onClick={togglePublicIP}
                                                disabled={isTogglingIP || !isVmActive}
                                                title={!isVmActive ? 'VM must be active to modify public IP' : ''}
                                            >
                                                {isTogglingIP 
                                                    ? 'Processing...' 
                                                    : vmDetails.floating_ip 
                                                        ? 'Remove IP' 
                                                        : 'Add IP'
                                                }
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {!isAdvancedRegion(vmDetails) && (
                                                <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-sm text-gray-400">Private IP Address</span>
                                                        <span className="text-xs text-[#cad9e3] bg-[rgba(202,217,227,0.1)] border border-[#1c2b22] px-2 py-1 rounded">Internal</span>
                                                    </div>
                                                    <div className="font-mono text-lg text-white">{sanitizeText(vmDetails.fixed_ip) || 'Not assigned'}</div>
                                                    <p className="text-xs text-gray-400 mt-2">Used for internal communication</p>
                                                </div>
                                            )}
                                            
                                            <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm text-gray-400">Public IP Address</span>
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        vmDetails.floating_ip 
                                                            ? 'status-assigned-pulse' 
                                                            : 'text-[#cad9e3] bg-[rgba(202,217,227,0.1)] border border-[#1c2b22]'
                                                    }`}>
                                                        {vmDetails.floating_ip ? 'Assigned' : 'Not assigned'}
                                                    </span>
                                                </div>
                                                <div className="font-mono text-lg text-white">
                                                    {sanitizeText(vmDetails.floating_ip) || 'None'}
                                                </div>
                                                <p className="text-xs text-gray-400 mt-2">
                                                    {vmDetails.floating_ip 
                                                        ? 'Accessible from the internet' 
                                                        : 'Add a public IP to access from internet'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-6 bg-[#0a0c0a]/60 border border-[#1a2a1a]/30 rounded-lg p-4 text-xs text-gray-400 leading-relaxed flex items-start">
                                            <Info className="h-4 w-4 mt-0.5 mr-2 text-[#e0eacc] flex-shrink-0" />
                                            <p>
                                                Public IPs allow your VM to be accessible from the internet.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Security Rules */}
                                {!isAdvancedRegion(vmDetails) && (
                                    <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.15)] rounded-xl shadow-xl p-4">
                                        <div className="border-b border-[#1c2b22] px-6 py-5 flex items-center justify-between">
                                            <h3 className="text-lg font-light text-[#e0eacc]">Security Rules</h3>
                                            <button 
                                                className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 ${isVmActive 
                                                    ? 'bg-[#cad9e3] text-[#040911] hover:bg-[#b8c7d3] shadow-sm hover:shadow' 
                                                    : 'bg-[#121a15]/50 text-gray-500 cursor-not-allowed border border-[#1a2a1a]/30'
                                                } font-medium`}
                                                onClick={() => isVmActive && setShowAddRuleModal(true)}
                                                title={!isVmActive ? "VM must be active to add security rules" : ""}
                                                disabled={!isVmActive}
                                            >
                                                Add Rule
                                            </button>
                                        </div>
                                        
                                        <div className="p-6">
{ruleActionStatus && (
    <div style={{
        marginBottom: '24px',
        padding: '16px',
        borderRadius: '8px',
        background: ruleActionStatus.type === 'success' 
            ? 'rgba(224, 234, 204, 0.08)' 
            : 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#d1d5db',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    }}>
        {ruleActionStatus.type === 'success' ? (
            <Check style={{ color: '#e0eacc', flexShrink: 0 }} size={20} />
        ) : (
            <Trash2 style={{ color: '#ef4444', flexShrink: 0 }} size={20} />
        )}
        <span>{sanitizeText(ruleActionStatus.message)}</span>
    </div>
)}
                                            
                                            {!isVmActive && (
                                                <div className="mb-6 bg-[#1a2a1a]/60 border border-[#1a2a1a]/30 text-gray-300 px-6 py-4 rounded-lg flex items-center">
                                                    <AlertTriangle className="h-5 w-5 mr-3 text-gray-400" />
                                                    <span className="text-gray-300">VM must be active to manage security rules</span>
                                                </div>
                                            )}
                                            
                                            {vmDetails.security_rules && vmDetails.security_rules.length > 0 ? (
                                                <div className="overflow-hidden rounded-lg border border-[#1a2a1a]/30">
                                                    <table className="w-full">
                                                        <thead>
                                                            <tr className="bg-[#121a15]/60">
                                                                <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Protocol</th>
                                                                <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Port Range</th>
                                                                <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Remote IP</th>
                                                                <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Direction</th>
                                                                <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-[#1a2a1a]/20">
                                                            {vmDetails.security_rules.map((rule, index) => (
                                                                <tr key={index} className={index % 2 === 0 ? "bg-[#0a0c0a]/50" : "bg-[#121a15]/30"}>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-white">{rule.protocol.toUpperCase()}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-white">
                                                                        {rule.port_range_min === rule.port_range_max 
                                                                            ? rule.port_range_min 
                                                                            : rule.port_range_min && rule.port_range_max
                                                                                ? `${rule.port_range_min}-${rule.port_range_max}`
                                                                                : 'All Ports'}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap font-mono text-white">{sanitizeText(rule.remote_ip_prefix) || '0.0.0.0/0'}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-white">{sanitizeText(rule.direction)}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                                        <button
    onClick={() => isVmActive && setSelectedRuleForDelete(rule)}
    className={`rounded-md font-medium transition-colors text-sm ${
        !isVmActive 
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
            : 'bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555]'
    } px-3 py-1.5`}
    disabled={!isVmActive || isDeletingRule === rule.id}
    title={!isVmActive ? "VM must be active to delete security rules" : ""}
>
    {isDeletingRule === rule.id ? 'Deleting...' : 'Remove'}
</button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="bg-[#121a15]/30 border border-[#1a2a1a]/30 rounded-lg p-6 text-gray-400 text-center">
                                                    <Info className="h-6 w-6 mx-auto mb-2 text-gray-500" />
                                                    <p>No security rules configured. Add a rule to control access to your VM.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Firewalls and Volumes grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Attached Firewalls */}
                                    {!isAdvancedRegion(vmDetails) && (
                                        <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.15)] rounded-xl shadow-xl p-4">
                                            <div className="border-b border-[#1c2b22] px-6 py-5">
                                                <h3 className="text-lg font-light text-[#e0eacc]">Attached Firewalls</h3>
                                            </div>
                                            
                                            <div className="p-6">
                                               {isLoadingFirewalls ? (
    <div className="bg-[#121a15]/30 border border-[#1a2a1a]/30 rounded-lg p-6 text-gray-400 text-center">
        <div className="animate-pulse">Loading firewalls...</div>
    </div>
) : attachedFirewalls.length > 0 ? (
    <div className={`space-y-4 overflow-y-auto custom-scrollbar ${attachedFirewalls.length > 4 ? 'max-h-[340px]' : ''}`}>
        {attachedFirewalls.map((firewall, index) => (
            <div key={index} className="bg-[#0a0c0a]/60 border border-[#1a2a1a]/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-white font-medium">{truncateText(sanitizeText(firewall.name), 50)}</h4>
                        <div className="mt-1 flex gap-6 text-xs text-gray-400">
                            <span>Region: <span className="text-white">{sanitizeText(firewall.environment?.region) || 'Unknown'}</span></span>
                            <span>Rules: <span className="text-white">{firewall.rules?.length || 0}</span></span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setSelectedFirewall(firewall);
                            setShowFirewallModal(true);
                        }}
                        className="px-3 py-1.5 bg-[#cad9e3] text-[#040911] rounded text-xs font-medium hover:bg-[#b8c7d3] transition-colors"
                    >
                        View Rules
                    </button>
                </div>
            </div>
        ))}
    </div>
) : (
                                                    <div className="bg-[#121a15]/30 border border-[#1a2a1a]/30 rounded-lg p-6 text-gray-400 text-center">
                                                        <Info className="h-5 w-5 mx-auto mb-2 text-gray-500" />
                                                        <p className="text-sm">No firewalls attached</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Attached Volumes */}
                                    <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.15)] rounded-xl shadow-xl p-4">
                                        <div className="border-b border-[#1c2b22] px-6 py-5">
                                            <h3 className="text-lg font-light text-[#e0eacc]">Attached Volumes</h3>
                                        </div>
                                        
                                        <div className="p-6">
               {vmDetails.volume_attachments && vmDetails.volume_attachments.length > 0 ? (
    <div className="rounded-lg border border-[#1a2a1a]/30 overflow-hidden">
        <table className="w-full">
            <thead className="sticky top-0 z-10">
                <tr className="bg-[#121a15]">
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Size</th>
<th className="px-4 py-3 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Type</th>
                </tr>
            </thead>
        </table>
        <div className={`overflow-y-auto custom-scrollbar ${vmDetails.volume_attachments.length > 4 ? 'max-h-[192px]' : ''}`}>
            <table className="w-full">
                <tbody className="divide-y divide-[#1a2a1a]/20">
                    {vmDetails.volume_attachments.map((attachment, index) => (
                        <tr key={index} className={index % 2 === 0 ? "bg-[#0a0c0a]/50" : "bg-[#121a15]/30"}>
                            <td className="px-4 py-3 text-white">{truncateText(sanitizeText(attachment.name), 50) || 'Unknown'}</td>
                            <td className="px-4 py-3 text-white">{attachment.size || 0} GB</td>
                            <td className="px-4 py-3 font-mono text-white text-xs">{sanitizeText(attachment.volume_type) || 'Unknown'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
) : (
                                                <div className="bg-[#121a15]/30 border border-[#1a2a1a]/30 rounded-lg p-6 text-gray-400 text-center">
                                                    <Info className="h-5 w-5 mx-auto mb-2 text-gray-500" />
                                                    <p className="text-sm">No volumes attached</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right column */}
                            <div className="lg:col-span-3 space-y-6">
                                {/* VM Status */}
                                <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.2)] rounded-xl shadow-xl">
                                    <div className="border-b border-[#1c2b22] px-6 py-5">
                                        <h3 className="text-lg font-light text-[#e0eacc]">VM Status</h3>
                                    </div>
                                    
                                    <div className="p-6">
                                        <div className="mb-6">
                                            <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm text-gray-400">Current Status</span>
                                                 <span className="bg-[#1a1a1a] border border-[rgba(202,217,227,0.3)] text-[#d1d5db] px-3 py-1.5 rounded-md text-sm font-medium">
    {sanitizeText(vmDetails.status)}
</span>
                                                </div>
                                                
                                           <p className="text-xs text-gray-400 leading-relaxed">
    {vmDetails.status === 'RUNNING' && 'Your VM is running and accessible.'}
    {vmDetails.status === 'SHUTOFF' && 'Your VM is stopped.'}
    {vmDetails.status === 'HIBERNATED' && 'Your VM is hibernated.'}
    {vmDetails.status === 'STARTING' && 'Your VM is starting up.'}
    {vmDetails.status === 'STOPPING' && 'Your VM is shutting down.'}
    {vmDetails.status === 'REBOOTING' && 'Your VM is rebooting.'}
    {vmDetails.status === 'RESTORING' && 'Your VM is restoring.'}
    {vmDetails.status === 'BUILD' && 'Your VM is being provisioned. This may take a few minutes.'}
    {vmDetails.status === 'ERROR' && 'Your VM encountered an error. Please contact support if this persists.'}
    {!['RUNNING', 'SHUTOFF', 'HIBERNATED', 'STARTING', 'STOPPING', 'REBOOTING', 'RESTORING', 'BUILD', 'ERROR'].includes(vmDetails.status) && 'VM is in a transitional state.'}
</p>
                                            </div>
                                        </div>
                                        
<div className="space-y-3">
    {/* Start VM Button */}
    <button 
        className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
            loadingStates.starting.has(vmDetails.instance_id) || !canStart
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
        }`}
        onClick={handleStartClick}
        disabled={loadingStates.starting.has(vmDetails.instance_id) || !canStart}
        title={
            loadingStates.starting.has(vmDetails.instance_id)
                ? 'VM is starting...'
                : !canStart
                    ? `Cannot start - VM is ${vmDetails.status}`
                    : 'Start this VM'
        }
    >
        {loadingStates.starting.has(vmDetails.instance_id) ? (
            <span className="flex items-center">
                <span className="animate-spin mr-2">⟳</span> Starting...
            </span>
        ) : (
            <span className="flex items-center">
                <Power className="h-4 w-4 mr-2" /> Start VM
            </span>
        )}
    </button>

    {/* Stop VM Button */}
    <button 
        className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
            loadingStates.stopping.has(vmDetails.instance_id) || !canStop
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
        }`}
        onClick={handleStopClick}
        disabled={loadingStates.stopping.has(vmDetails.instance_id) || !canStop}
        title={
            loadingStates.stopping.has(vmDetails.instance_id)
                ? 'VM is stopping...'
                : !canStop
                    ? `Cannot stop - VM is ${vmDetails.status}`
                    : 'Stop this VM'
        }
    >
        {loadingStates.stopping.has(vmDetails.instance_id) ? (
            <span className="flex items-center">
                <span className="animate-spin mr-2">⟳</span> Stopping...
            </span>
        ) : (
            <span className="flex items-center">
                <Power className="h-4 w-4 mr-2" /> Stop VM
            </span>
        )}
    </button>
    
    {/* Reboot VM Button - Legacy Only */}
    {!isAdvancedRegion(vmDetails) && (
        <button 
            className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
                loadingStates.rebooting.has(vmDetails.instance_id) || !canReboot
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
            }`}
            onClick={handleRebootClick}
            disabled={loadingStates.rebooting.has(vmDetails.instance_id) || !canReboot}
            title={
                loadingStates.rebooting.has(vmDetails.instance_id) 
                    ? 'VM is rebooting...' 
                    : !canReboot
                        ? `Cannot reboot - VM is ${vmDetails.status}`
                        : 'Reboot this VM'
            }
        >
            {loadingStates.rebooting.has(vmDetails.instance_id) ? (
                <span className="flex items-center">
                    <span className="animate-spin mr-2">⟳</span> Rebooting...
                </span>
            ) : (
                <span className="flex items-center">
                    <Power className="h-4 w-4 mr-2" /> Reboot VM
                </span>
            )}
        </button>
    )}

    {/* Restore VM Button - Legacy Hibernated Only */}
    {!isAdvancedRegion(vmDetails) && canRestore && (
        <button 
            className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
                loadingStates.restoring.has(vmDetails.instance_id)
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
            }`}
            onClick={handleRestoreClick}
            disabled={loadingStates.restoring.has(vmDetails.instance_id)}
            title={
                loadingStates.restoring.has(vmDetails.instance_id) 
                    ? 'VM is restoring...' 
                    : 'Restore this VM'
            }
        >
            {loadingStates.restoring.has(vmDetails.instance_id) ? (
                <span className="flex items-center">
                    <span className="animate-spin mr-2">⟳</span> Restoring...
                </span>
            ) : (
                <span className="flex items-center">
                    <Power className="h-4 w-4 mr-2" /> Restore VM
                </span>
            )}
        </button>
    )}
</div>                                    </div>
                                </div>
                                
                                {/* Cost Calculator */}
                                <div className="bg-gradient-to-br from-[#0a0c0a] to-[#121a15] border border-[rgba(202,217,227,0.15)] rounded-xl shadow-xl p-4">
                                    <div className="border-b border-[#1c2b22] px-6 py-5">
                                        <h3 className="text-lg font-light text-[#e0eacc]">Cost Calculator</h3>
                                    </div>
                                    
                                    <div className="p-6">
                                        {vmDetails.specs?.gpu_type && vmDetails.specs?.gpu_count > 0 ? (
                                            <div>
                                                <div className="bg-[#121a15]/60 border border-[#1a2a1a]/30 rounded-lg p-5">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <span className="text-sm text-gray-400">GPU:  </span>
                                                        <span className="text-sm text-white font-medium">
                                                            {vmDetails.specs.gpu_count}× {sanitizeText(vmDetails.specs.gpu_type)}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Usage Period</label>
                                                        <select 
    className="w-full bg-[#0a0c0a] border border-[#1a2a1a]/50 rounded text-white py-2.5 px-3 text-sm focus:outline-none focus:border-[#e0eacc]/50 transition-colors"
    value={usagePeriod}
    onChange={(e) => handlePeriodChange(e.target.value)}
>
    <option value="hour">Hours</option>
    <option value="day">Days</option>
    <option value="month">Months</option>
    <option value="year">Years</option>
</select>
                                                        </div>
                                                        
                                                        <div>
                                                            <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">
                                                                {usagePeriod === 'hour' ? 'Number of Hours' : 
                                                                usagePeriod === 'day' ? 'Number of Days' : 
                                                                usagePeriod === 'month' ? 'Number of Months' : 'Number of Years'}
                                                            </label>
                                                        <input
    type="text"
    inputMode="numeric"
    value={inputValue}
    onChange={handleInputChange}
    onFocus={handleInputFocus}
    onBlur={handleInputBlur}
    onKeyDown={handleInputKeyDown}
    className="w-full bg-[#0a0c0a] border border-[#1a2a1a]/50 rounded text-white py-2.5 px-3 text-sm focus:outline-none focus:border-[#e0eacc]/50 transition-colors"
    style={{
        borderColor: isInputValid ? '' : '#ff4444'
    }}
    maxLength={5}
/>
                                                        </div>
                                                        
                                                        <div className="pt-3 border-t border-[#1a2a1a]/30 space-y-2">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-gray-400">Hourly Rate</span>
                                                                <span className="text-sm text-[#e0eacc] font-medium">
                                                                    {vmCost.symbol}{vmCost.hourly.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-gray-400">Per Minute</span>
                                                                <span className="text-sm text-white">
                                                                    {vmCost.symbol}{(vmCost.hourly / 60).toFixed(4)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="pt-3 border-t border-[#1a2a1a]/30">
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-xs text-gray-400">Total Cost</span>
                                                                <span className="text-xs text-gray-400">
                                                                    ({getUsageHours()} hours)
                                                                </span>
                                                            </div>
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-white">Total</span>
                                                                {needsCustomPricing() ? (
                                                                    <div className="text-right">
                                                                        <span className="text-[#e0eacc] font-semibold text-lg line-through">
                                                                            {vmCost.symbol}{calculateTotalCost().toFixed(2)}
                                                                        </span>
                                                                        <div className="text-[#4a9eff] text-xs font-medium mt-0.5">
                                                                            Custom Pricing Available
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-[#e0eacc] font-semibold text-lg">
                                                                        {vmCost.symbol}{calculateTotalCost().toFixed(2)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                      
                                            </div>
                                        ) : (
                                            <div className="bg-[#121a15]/30 border border-[#1a2a1a]/30 rounded-lg p-6 text-center">
                                                <Info className="h-6 w-6 mx-auto mb-2 text-gray-500" />
                                                <p className="text-gray-400 text-sm">No GPU detected. Cost calculations not available.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // MOBILE LAYOUT - Complete implementation
                <div className="min-h-screen bg-black text-white">
                    <div className={`max-w-6xl mx-auto ${isMobile ? 'p-4' : 'p-6'}`}>
                        {/* Mobile Header */}
                        <div className={`mb-8 bg-[#121212] border border-[#333333] rounded-xl shadow-xl ${isMobile ? 'p-6' : 'p-8'}`}>
                            <div className="flex items-center mb-6">
                                <div className={`bg-[#222222] border border-[#333333] rounded-full shadow-sm mr-4 ${isMobile ? 'p-3' : 'p-4'}`}>
                                    <svg className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`} viewBox="0 0 24 24" fill="none">
                                        <rect x="2" y="4" width="20" height="4" rx="1" fill="#cad9e3"/>
                                        <rect x="2" y="10" width="20" height="4" rx="1" fill="#cad9e3"/>
                                        <rect x="2" y="16" width="20" height="4" rx="1" fill="#cad9e3"/>
                                        <circle cx="6" cy="6" r="0.5" fill="#121a15"/>
                                        <circle cx="8" cy="6" r="0.5" fill="#121a15"/>
                                        <circle cx="6" cy="12" r="0.5" fill="#121a15"/>
                                        <circle cx="8" cy="12" r="0.5" fill="#121a15"/>
                                        <circle cx="6" cy="18" r="0.5" fill="#121a15"/>
                                        <circle cx="8" cy="18" r="0.5" fill="#121a15"/>
                                    </svg>
                                </div>
                                <div>
                                    <h1 className={`font-medium text-white ${isMobile ? 'text-2xl' : 'text-3xl'}`}>
                                        {truncateText(sanitizeText(vmDetails.name), 80)}
                                    </h1>
                                    <p className={`text-gray-400 mt-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
                                        VM ID: {sanitizeText(vmDetails.instance_id || 'N/A')} | Created: {formatDate(vmDetails.created_at)}
                                    </p>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3">
                                <button 
                                    className="w-full px-4 py-3 bg-[#0a0c0a] text-white border border-[rgba(202,217,227,0.3)] rounded-md font-medium hover:bg-[#121a15] transition-colors"
                                    onClick={onBack}
                                >
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>

                        {/* Mobile Stats Cards */}
                        <div className={`grid gap-6 mb-8 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
                            <div className="bg-gradient-to-br from-[#1e1e1e] to-[#0f0f0f] rounded-xl border border-[#333333] shadow-[0_8px_32px_rgba(0,0,0,0.6)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.8)] transition-all duration-300 hover:-translate-y-1 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/5 before:to-transparent before:pointer-events-none">
                                <div className={`relative z-10 ${isMobile ? 'p-4' : 'p-5'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className={`text-gray-400 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>CPU Cores</p>
                                            <p className={`font-bold text-white ${isMobile ? 'text-lg' : 'text-2xl'}`}>{vmDetails.specs?.cpu || 0}</p>
                                        </div>
                                        <div className={`bg-gradient-to-br from-[#333333] to-[#555555] rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.3)] ${isMobile ? 'p-2' : 'p-3'}`}>
                                            <Server className="text-[#e0eacc]" size={isMobile ? 14 : 20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-[#1e1e1e] to-[#0f0f0f] rounded-xl border border-[#333333] shadow-[0_8px_32px_rgba(0,0,0,0.6)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.8)] transition-all duration-300 hover:-translate-y-1 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/5 before:to-transparent before:pointer-events-none">
                                <div className={`relative z-10 ${isMobile ? 'p-4' : 'p-5'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className={`text-gray-400 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Memory</p>
                                            <p className={`font-bold text-white ${isMobile ? 'text-lg' : 'text-2xl'}`}>{vmDetails.specs?.ram || 0} GB</p>
                                        </div>
                                        <div className={`bg-gradient-to-br from-[#333333] to-[#555555] rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.3)] ${isMobile ? 'p-2' : 'p-3'}`}>
                                            <Database className="text-[#e0eacc]" size={isMobile ? 14 : 20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-[#1e1e1e] to-[#0f0f0f] rounded-xl border border-[#333333] shadow-[0_8px_32px_rgba(0,0,0,0.6)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.8)] transition-all duration-300 hover:-translate-y-1 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/5 before:to-transparent before:pointer-events-none">
                                <div className={`relative z-10 ${isMobile ? 'p-4' : 'p-5'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            {isAdvancedRegion(vmDetails) ? (
                                                <>
                                                    <p className={`text-gray-400 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>GPU Memory</p>
                                                    <p className={`font-bold text-white ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                                                        {(() => {
                                                            const gpuString = vmDetails.specs?.gpu_type || '';
                                                            const memoryMatch = gpuString.match(/(\d+)GB/);
                                                            return memoryMatch ? `${memoryMatch[1]} GB` : 'N/A';
                                                        })()}
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <p className={`text-gray-400 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Storage</p>
                                                    <p className={`font-bold text-white ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                                                        {vmDetails.volume_attachments?.reduce((sum, vol) => sum + (vol.size || 0), 0) || 0} GB
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                        <div className={`bg-gradient-to-br from-[#333333] to-[#555555] rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.3)] ${isMobile ? 'p-2' : 'p-3'}`}>
                                            <Database className="text-[#e0eacc]" size={isMobile ? 14 : 20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-[#1e1e1e] to-[#0f0f0f] rounded-xl border border-[#333333] shadow-[0_8px_32px_rgba(0,0,0,0.6)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.8)] transition-all duration-300 hover:-translate-y-1 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/5 before:to-transparent before:pointer-events-none">
                                <div className={`relative z-10 ${isMobile ? 'p-4' : 'p-5'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className={`text-gray-400 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Status</p>
                                            <p className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'} ${
                                                isVmActive ? 'text-green-400' : 
                                                isVmStopped ? 'text-red-400' : 
                                                isVmHibernated ? 'text-yellow-400' : 'text-gray-400'
 }`}>{sanitizeText(vmDetails.status)}</p>
                                        </div>
                                        <div className={`bg-gradient-to-br from-[#333333] to-[#555555] rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.3)] ${isMobile ? 'p-2' : 'p-3'}`}>
                                            <Circle className={`${
                                                isVmActive ? 'text-green-400 fill-green-400' : 
                                                isVmStopped ? 'text-red-400 fill-red-400' : 
                                                isVmHibernated ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400 fill-gray-400'
                                            }`} size={isMobile ? 14 : 20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* VM Status - Mobile */}
                        <div className="bg-[#121212] border border-[#333333] rounded-xl shadow-xl mb-8">
                            <div className={`border-b border-[#333333] ${isMobile ? 'px-6 py-4' : 'px-6 py-5'}`}>
                                <h3 className={`font-medium text-[#e0eacc] ${isMobile ? 'text-lg' : 'text-xl'}`}>VM Controls</h3>
                            </div>
                            
                            <div className={isMobile ? 'p-6' : 'p-8'}>
                                <div className="mb-6 bg-[#1a1a1a] rounded-lg border border-[#333333] p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm text-gray-400">Current Status</span>
                                        <span className={`flex items-center text-sm font-medium ${
                                            isVmActive ? 'text-green-400' : 
                                            isVmStopped ? 'text-red-400' : 
                                            isVmHibernated ? 'text-yellow-400' : 'text-gray-400'
                                        }`}>
                                            <Circle className={`h-3 w-3 mr-2 ${
                                                isVmActive ? 'text-green-400 fill-green-400' : 
                                                isVmStopped ? 'text-red-400 fill-red-400' : 
                                                isVmHibernated ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400 fill-gray-400'
                                            }`} />
                                            {sanitizeText(vmDetails.status)}
                                        </span>
                                    </div>
                                    
                          <p className="text-xs text-gray-400 leading-relaxed">
    {vmDetails.status === 'RUNNING' && 'Your VM is running and accessible.'}
    {vmDetails.status === 'SHUTOFF' && 'Your VM is stopped.'}
    {vmDetails.status === 'HIBERNATED' && 'Your VM is hibernated.'}
    {vmDetails.status === 'STARTING' && 'Your VM is starting up.'}
    {vmDetails.status === 'STOPPING' && 'Your VM is shutting down.'}
    {vmDetails.status === 'REBOOTING' && 'Your VM is rebooting.'}
    {vmDetails.status === 'RESTORING' && 'Your VM is restoring.'}
    {vmDetails.status === 'BUILD' && 'Your VM is being provisioned. This may take a few minutes.'}
    {vmDetails.status === 'ERROR' && 'Your VM encountered an error. Please contact support if this persists.'}
    {!['RUNNING', 'SHUTOFF', 'HIBERNATED', 'STARTING', 'STOPPING', 'REBOOTING', 'RESTORING', 'BUILD', 'ERROR'].includes(vmDetails.status) && 'VM is in a transitional state.'}
</p>
                                </div>
                             <div className="space-y-3">
    {/* Start VM Button */}
    <button 
        className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
            loadingStates.starting.has(vmDetails.instance_id) || !canStart
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
        }`}
        onClick={handleStartClick}
        disabled={loadingStates.starting.has(vmDetails.instance_id) || !canStart}
        title={
            loadingStates.starting.has(vmDetails.instance_id)
                ? 'VM is starting...'
                : !canStart
                    ? `Cannot start - VM is ${vmDetails.status}`
                    : 'Start this VM'
        }
    >
        {loadingStates.starting.has(vmDetails.instance_id) ? (
            <span className="flex items-center">
                <span className="animate-spin mr-2">⟳</span> Starting...
            </span>
        ) : (
            <span className="flex items-center">
                <Power className="h-4 w-4 mr-2" /> Start VM
            </span>
        )}
    </button>

    {/* Stop VM Button */}
    <button 
        className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
            loadingStates.stopping.has(vmDetails.instance_id) || !canStop
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
        }`}
        onClick={handleStopClick}
        disabled={loadingStates.stopping.has(vmDetails.instance_id) || !canStop}
        title={
            loadingStates.stopping.has(vmDetails.instance_id)
                ? 'VM is stopping...'
                : !canStop
                    ? `Cannot stop - VM is ${vmDetails.status}`
                    : 'Stop this VM'
        }
    >
        {loadingStates.stopping.has(vmDetails.instance_id) ? (
            <span className="flex items-center">
                <span className="animate-spin mr-2">⟳</span> Stopping...
            </span>
        ) : (
            <span className="flex items-center">
                <Power className="h-4 w-4 mr-2" /> Stop VM
            </span>
        )}
    </button>
    
    {/* Reboot VM Button - Legacy Only */}
    {!isAdvancedRegion(vmDetails) && (
        <button 
            className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
                loadingStates.rebooting.has(vmDetails.instance_id) || !canReboot
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
            }`}
            onClick={handleRebootClick}
            disabled={loadingStates.rebooting.has(vmDetails.instance_id) || !canReboot}
            title={
                loadingStates.rebooting.has(vmDetails.instance_id) 
                    ? 'VM is rebooting...' 
                    : !canReboot
                        ? `Cannot reboot - VM is ${vmDetails.status}`
                        : 'Reboot this VM'
            }
        >
            {loadingStates.rebooting.has(vmDetails.instance_id) ? (
                <span className="flex items-center">
                    <span className="animate-spin mr-2">⟳</span> Rebooting...
                </span>
            ) : (
                <span className="flex items-center">
                    <Power className="h-4 w-4 mr-2" /> Reboot VM
                </span>
            )}
        </button>
    )}

    {/* Restore VM Button - Legacy Hibernated Only */}
    {!isAdvancedRegion(vmDetails) && canRestore && (
        <button 
            className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center ${
                loadingStates.restoring.has(vmDetails.instance_id)
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
            }`}
            onClick={handleRestoreClick}
            disabled={loadingStates.restoring.has(vmDetails.instance_id)}
            title={
                loadingStates.restoring.has(vmDetails.instance_id) 
                    ? 'VM is restoring...' 
                    : 'Restore this VM'
            }
        >
            {loadingStates.restoring.has(vmDetails.instance_id) ? (
                <span className="flex items-center">
                    <span className="animate-spin mr-2">⟳</span> Restoring...
                </span>
            ) : (
                <span className="flex items-center">
                    <Power className="h-4 w-4 mr-2" /> Restore VM
                </span>
            )}
        </button>
    )}
</div>
                            </div>
                        </div>

                        {/* Network & Connectivity */}
                        <div className="bg-[#121212] border border-[#333333] rounded-xl shadow-xl mb-8">
                            <div className={`border-b border-[#333333] flex items-center justify-between ${isMobile ? 'px-6 py-4' : 'px-6 py-5'}`}>
                                <h3 className={`font-medium text-[#e0eacc] ${isMobile ? 'text-lg' : 'text-xl'}`}>Network & Connectivity</h3>
                                {!isAdvancedRegion(vmDetails) && (
                                    <button 
                                        className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 font-medium ${
                                            !isVmActive 
                                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                                                : vmDetails.floating_ip 
                                                    ? 'bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)]'
                                                    : 'bg-[#e0eacc] text-[#040911] hover:bg-[#d0dab9]'
                                        }`}
                                        onClick={togglePublicIP}
                                        disabled={isTogglingIP || !isVmActive}
                                        title={!isVmActive ? 'VM must be active to modify public IP' : ''}
                                    >
                                        {isTogglingIP 
                                            ? 'Processing...' 
                                            : vmDetails.floating_ip 
                                                ? 'Remove Public IP' 
                                                : 'Add Public IP'
                                        }
                                    </button>
                                )}
                            </div>
                            
                            <div className={isMobile ? 'p-6' : 'p-8'}>
                                <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                    {!isAdvancedRegion(vmDetails) && (
                                        <div className="bg-[#1a1a1a] rounded-lg border border-[#333333] p-5">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-sm text-gray-400">Private IP Address</span>
                                                <span className="text-xs text-[#cad9e3] bg-[rgba(202,217,227,0.1)] border border-[#333333] px-2 py-1 rounded">Internal</span>
                                            </div>
                                            <div className="font-mono text-lg text-white">{sanitizeText(vmDetails.fixed_ip) || 'Not assigned'}</div>
                                            <p className="text-xs text-gray-400 mt-2">Used for internal communication within your VPC</p>
                                        </div>
                                    )}
                                    
                                    <div className="bg-[#1a1a1a] rounded-lg border border-[#333333] p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm text-gray-400">Public IP Address</span>
                                            <span className={`text-xs px-2 py-1 rounded ${
                                                vmDetails.floating_ip 
                                                    ? 'status-assigned-pulse' 
                                                    : 'text-[#cad9e3] bg-[rgba(202,217,227,0.1)] border border-[#333333]'
                                            }`}>
                                                {vmDetails.floating_ip ? 'Assigned' : 'Not assigned'}
                                            </span>
                                        </div>
                                        <div className="font-mono text-lg text-white">
                                            {sanitizeText(vmDetails.floating_ip) || 'None'}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">
                                            {vmDetails.floating_ip 
                                                ? 'Accessible from the internet' 
                                                : 'Add a public IP to access from internet'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Security Rules */}
                        {!isAdvancedRegion(vmDetails) && (
                            <div className="bg-[#121212] border border-[#333333] rounded-xl shadow-xl mb-8">
                                <div className={`border-b border-[#333333] flex items-center justify-between ${isMobile ? 'px-6 py-4' : 'px-6 py-5'}`}>
                                    <h3 className={`font-medium text-[#e0eacc] ${isMobile ? 'text-lg' : 'text-xl'}`}>Security Rules</h3>
                                    <button 
                                        className={`rounded-md font-medium transition-colors ${isVmActive 
                                            ? 'bg-[#cad9e3] text-black hover:bg-[#b8c7d3]' 
                                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                        } ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                                        onClick={() => isVmActive && setShowAddRuleModal(true)}
                                        title={!isVmActive ? "VM must be active to add security rules" : ""}
                                        disabled={!isVmActive}
                                    >
                                        Add Rule
                                    </button>
                                </div>
                                
                                <div className={isMobile ? 'p-6' : 'p-8'}>
                                 {ruleActionStatus && (
    <div style={{
        marginBottom: '24px',
        padding: '16px',
        borderRadius: '8px',
        background: ruleActionStatus.type === 'success' 
            ? 'rgba(224, 234, 204, 0.08)' 
            : 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#d1d5db',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    }}>
        {ruleActionStatus.type === 'success' ? (
            <Check style={{ color: '#e0eacc', flexShrink: 0 }} size={20} />
        ) : (
            <Trash2 style={{ color: '#ef4444', flexShrink: 0 }} size={20} />
        )}
        <span>{sanitizeText(ruleActionStatus.message)}</span>
    </div>
)}
                                    
                                    {!isVmActive && (
                                        <div className="mb-6 bg-[#1a1a1a] border border-[#333333] text-gray-300 px-6 py-4 rounded-lg flex items-center">
                                            <AlertTriangle className="h-5 w-5 mr-3 text-gray-400" />
                                            <span className="text-gray-300">VM must be active to manage security rules</span>
                                        </div>
                                    )}
                                    
{vmDetails.security_rules && vmDetails.security_rules.length > 0 ? (
    <div className="rounded-lg border border-[#1a2a1a]/30 overflow-hidden">
        <table className="w-full">
            <thead className="sticky top-0 z-10">
                <tr className="bg-[#121a15]">
                    <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Protocol</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Port Range</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Remote IP</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Direction</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-[#e0eacc] uppercase tracking-wider">Actions</th>
                </tr>
            </thead>
        </table>
        <div className={`overflow-y-auto custom-scrollbar ${vmDetails.security_rules.length > 4 ? 'max-h-[224px]' : ''}`}>
            <table className="w-full">
                <tbody className="divide-y divide-[#1a2a1a]/20">
                    {vmDetails.security_rules.map((rule, index) => (
                        <tr key={index} className={index % 2 === 0 ? "bg-[#0a0c0a]/50" : "bg-[#121a15]/30"}>
                            <td className="px-6 py-4 whitespace-nowrap text-white">{rule.protocol.toUpperCase()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-white">
                                {rule.port_range_min === rule.port_range_max 
                                    ? rule.port_range_min 
                                    : rule.port_range_min && rule.port_range_max
                                        ? `${rule.port_range_min}-${rule.port_range_max}`
                                        : 'All Ports'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-mono text-white">{sanitizeText(rule.remote_ip_prefix) || '0.0.0.0/0'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-white">{sanitizeText(rule.direction)}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <button
    onClick={() => isVmActive && setSelectedRuleForDelete(rule)}
    className={`rounded-md font-medium transition-colors text-sm ${
        !isVmActive 
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
            : 'bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555]'
    } px-3 py-1.5`}
    disabled={!isVmActive || isDeletingRule === rule.id}
    title={!isVmActive ? "VM must be active to delete security rules" : ""}
>
    {isDeletingRule === rule.id ? 'Deleting...' : 'Remove'}
</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
) : (                                        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-gray-400 text-center">
                                            <Info className="h-6 w-6 mx-auto mb-2 text-gray-500" />
                                            <p>No security rules configured. Add a rule to control access to your VM.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Bottom Grid - Firewalls and Volumes */}
                        <div className={`grid gap-8 mb-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                            {/* Attached Firewalls */}
                            {!isAdvancedRegion(vmDetails) && (
                                <div className="bg-[#121212] border border-[#333333] rounded-xl shadow-xl">
                                    <div className={`border-b border-[#333333] ${isMobile ? 'px-6 py-4' : 'px-6 py-5'}`}>
                                        <h3 className={`font-medium text-[#e0eacc] ${isMobile ? 'text-lg' : 'text-xl'}`}>Attached Firewalls</h3>
                                    </div>
                                    
                                    <div className={isMobile ? 'p-6' : 'p-8'}>
{isLoadingFirewalls ? (
    <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-gray-400 text-center">
        <div className="animate-pulse">Loading firewalls...</div>
    </div>
) : attachedFirewalls.length > 0 ? (
    <div className={`space-y-4 overflow-y-auto custom-scrollbar ${attachedFirewalls.length > 4 ? 'max-h-[400px]' : ''}`}>
        {attachedFirewalls.map((firewall, index) => (
            <div key={index} className="bg-[#1a1a1a] rounded-lg border border-[#333333] p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-white font-medium">{truncateText(sanitizeText(firewall.name), 50)}</h4>
                        <div className="mt-2 flex gap-6 text-sm text-gray-400">
                            <span>Region: <span className="text-white">{sanitizeText(firewall.environment?.region) || 'Unknown'}</span></span>
                            <span>Rules: <span className="text-white">{firewall.rules?.length || 0}</span></span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setSelectedFirewall(firewall);
                            setShowFirewallModal(true);
                        }}
                        className="px-3 py-2 bg-[#cad9e3] text-black rounded font-medium hover:bg-[#b8c7d3] transition-colors text-sm"
                    >
                        View Rules
                    </button>
                </div>
            </div>
        ))}
    </div>
) : (
                                            <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-gray-400 text-center">
                                                <Info className="h-6 w-6 mx-auto mb-2 text-gray-500" />
                                                <p>No firewalls attached</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Attached Volumes */}
                            <div className="bg-[#121212] border border-[#333333] rounded-xl shadow-xl">
                                <div className={`border-b border-[#333333] ${isMobile ? 'px-6 py-4' : 'px-6 py-5'}`}>
                                    <h3 className={`font-medium text-[#e0eacc] ${isMobile ? 'text-lg' : 'text-xl'}`}>Attached Volumes</h3>
                                </div>
                                
                                <div className={isMobile ? 'p-6' : 'p-8'}>
                                    {vmDetails.volume_attachments && vmDetails.volume_attachments.length > 0 ? (
                                        <div className="space-y-4">
                                            {vmDetails.volume_attachments.map((attachment, index) => (
                                                <div key={index} className="bg-[#1a1a1a] rounded-lg border border-[#333333] p-4">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <h4 className="text-white font-medium">{truncateText(sanitizeText(attachment.name), 50) || 'Unknown'}</h4>
                                                        <span className="text-gray-400 text-sm">{attachment.size || 0} GB</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <span className="text-gray-400">Type:</span>
                                                            <span className="text-white ml-2">{sanitizeText(attachment.volume_type) || 'Standard'}</span>
                                                        </div>
                                                  <div>
    <span className="text-gray-400">Type:</span>
    <span className="text-white ml-2">{sanitizeText(attachment.volume_type) || 'Unknown'}</span>
</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-gray-400 text-center">
                                            <Info className="h-6 w-6 mx-auto mb-2 text-gray-500" />
                                            <p>No volumes attached</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Cost Calculator */}
                        <div className="bg-[#121212] border border-[#333333] rounded-xl shadow-xl mb-8">
                            <div className={`border-b border-[#333333] ${isMobile ? 'px-6 py-4' : 'px-6 py-5'}`}>
                                <h3 className={`font-medium text-[#e0eacc] ${isMobile ? 'text-lg' : 'text-xl'}`}>Cost Calculator</h3>
                            </div>
                            
                            <div className={isMobile ? 'p-6' : 'p-8'}>
                                {vmDetails.specs?.gpu_type && vmDetails.specs?.gpu_count > 0 ? (
                                    <div>
                                        <div className="bg-[#1a1a1a] rounded-lg border border-[#333333] p-5 mb-6">
                                            <div className="flex justify-between items-center mb-4">
                                                <span className="text-sm text-gray-400">GPU:  </span>
                                                <span className="text-sm text-white font-medium">
                                                    {vmDetails.specs.gpu_count}× {sanitizeText(vmDetails.specs.gpu_type)}
                                                </span>
                                            </div>
                                            
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Usage Period</label>
                                                    <select 
                                                        className="w-full bg-[#0a0c0a] border border-[#333333] rounded text-white py-3 px-3 text-sm focus:outline-none focus:border-[#e0eacc] transition-colors"
                                                        value={usagePeriod}
onChange={(e) => handlePeriodChange(e.target.value)}
                                                    >
                                                        <option value="hour">Hours</option>
                                                        <option value="day">Days</option>
                                                        <option value="month">Months</option>
                                                        <option value="year">Years</option>
                                                    </select>
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">
                                                        {usagePeriod === 'hour' ? 'Number of Hours' : 
                                                        usagePeriod === 'day' ? 'Number of Days' : 
                                                        usagePeriod === 'month' ? 'Number of Months' : 'Number of Years'}
                                                    </label>
                                                   <input
    type="text"
    inputMode="numeric"
    value={inputValue}
    onChange={handleInputChange}
    onFocus={handleInputFocus}
    onBlur={handleInputBlur}
    onKeyDown={handleInputKeyDown}
    className="w-full bg-[#0a0c0a] border border-[#1a2a1a]/50 rounded text-white py-2.5 px-3 text-sm focus:outline-none focus:border-[#e0eacc]/50 transition-colors"
    style={{
        borderColor: isInputValid ? '' : '#ff4444'
    }}
    maxLength={5}
/>
                                                </div>
                                                
                                                <div className="pt-4 border-t border-[#333333] space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-400">Hourly Rate</span>
                                                        <span className="text-sm text-[#e0eacc] font-medium">
                                                            {vmCost.symbol}{vmCost.hourly.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-400">Per Minute</span>
                                                        <span className="text-sm text-white">
                                                            {vmCost.symbol}{(vmCost.hourly / 60).toFixed(4)}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <div className="pt-4 border-t border-[#333333]">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-xs text-gray-400">Total Cost</span>
                                                        <span className="text-xs text-gray-400">
                                                            ({getUsageHours()} hours)
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-white font-medium">Total</span>
                                                        {needsCustomPricing() ? (
                                                            <div className="text-right">
                                                                <span className="text-[#e0eacc] font-semibold text-lg line-through">
                                                                    {vmCost.symbol}{calculateTotalCost().toFixed(2)}
                                                                </span>
                                                                <div className="text-[#4a9eff] text-xs font-medium mt-1">
                                                                    Custom Pricing Available
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-[#e0eacc] font-semibold text-xl">
                                                                {vmCost.symbol}{calculateTotalCost().toFixed(2)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                    +
                                    </div>
                                ) : (
                                    <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-center">
                                        <Info className="h-6 w-6 mx-auto mb-2 text-gray-500" />
                                        <p className="text-gray-400">No GPU detected. Cost calculations not available.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Rule Modal - Mobile Optimized */}
{/* Add Rule Modal - Mobile Optimized */}
{showAddRuleModal && (
    <>
        <style>
            {`
              .custom-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: #555555 #2a2a2a;
              }
              .custom-scrollbar::-webkit-scrollbar {
                width: 6px;
              }
              .custom-scrollbar::-webkit-scrollbar-track {
                background: #2a2a2a;
                border-radius: 3px;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: #555555;
                border-radius: 3px;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: #666666;
              }
            `}
        </style>
      <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: isMobile ? '16px' : '0'
}}>
    <div style={{
        backgroundColor: '#121212',
        borderRadius: '8px',
        border: '1px solid #333333',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: isMobile ? '100%' : '421px',
        maxHeight: isMobile ? '96vh' : 'auto',
        overflowY: isMobile ? 'auto' : 'visible'
    }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #222222',
                    padding: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            borderRadius: '50%',
                            backgroundColor: '#1a1a1a',
                            marginRight: '10px',
                            padding: '6px'
                        }}>
                            <svg width="20" height="24" viewBox="0 0 24 28">
                                <path
                                    d="M12 0L0 4v10c0 6 5 12 12 14 7-2 12-8 12-14V4L12 0z"
                                    fill="#e0eacc"
                                    stroke="#333333"
                                    strokeWidth="1.5"
                                />
                            </svg>
                        </div>
                        <h2 style={{
                            fontWeight: 600,
                            color: 'white',
                            fontSize: '18px',
                            margin: 0
                        }}>Add Security Rule</h2>
                    </div>
                    <button
                        onClick={() => {
                            setShowAddRuleModal(false);
                            setAddRuleError(null);
                        }}
                        style={{
                            padding: '8px',
                            borderRadius: '50%',
                            background: 'none',
                            border: 'none',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            fontSize: '24px',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px'
                        }}
                    >×</button>
                </div>
                
                {/* Content */}
                <div style={{ padding: '16px' }}>
                    {/* Grid Layout */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '12px',
                        marginBottom: '12px'
                    }}>
                        {/* Direction Field */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{
                                color: '#d1d5db',
                                fontWeight: 500,
                                fontSize: '13px',
                                marginBottom: '6px'
                            }}>
                                Direction <span style={{ color: '#ff8080' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    value={newRule.direction}
                                    onChange={(e) => setNewRule({...newRule, direction: e.target.value})}
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#1d1d1d',
                                        border: '1px solid #333333',
                                        borderRadius: '4px',
                                        padding: '10px',
                                        paddingRight: '36px',
                                        color: 'white',
                                        fontSize: '14px',
                                        outline: 'none',
                                        appearance: 'none',
                                        cursor: 'pointer',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                                    onBlur={(e) => e.target.style.borderColor = '#333333'}
                                >
                                    <option value="ingress">Ingress</option>
                                    <option value="egress">Egress</option>
                                </select>
                                <svg 
                                    width="10" 
                                    height="6" 
                                    viewBox="0 0 10 6" 
                                    fill="none"
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <path d="M1 1L5 5L9 1" stroke="#777777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        {/* Protocol Field */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{
                                color: '#d1d5db',
                                fontWeight: 500,
                                fontSize: '13px',
                                marginBottom: '6px'
                            }}>
                                Protocol <span style={{ color: '#ff8080' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    value={newRule.protocol}
                                    onChange={(e) => setNewRule({...newRule, protocol: e.target.value})}
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#1d1d1d',
                                        border: '1px solid #333333',
                                        borderRadius: '4px',
                                        padding: '10px',
                                        paddingRight: '36px',
                                        color: 'white',
                                        fontSize: '14px',
                                        outline: 'none',
                                        appearance: 'none',
                                        cursor: 'pointer',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                                    onBlur={(e) => e.target.style.borderColor = '#333333'}
                                >
                                    <option value="tcp">TCP</option>
                                    <option value="udp">UDP</option>
                                    <option value="icmp">ICMP</option>
                                </select>
                                <svg 
                                    width="10" 
                                    height="6" 
                                    viewBox="0 0 10 6" 
                                    fill="none"
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <path d="M1 1L5 5L9 1" stroke="#777777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        {/* IP Version Field */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{
                                color: '#d1d5db',
                                fontWeight: 500,
                                fontSize: '13px',
                                marginBottom: '6px'
                            }}>
                                IP Version <span style={{ color: '#ff8080' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    value={newRule.ethertype}
                                    onChange={(e) => setNewRule({...newRule, ethertype: e.target.value})}
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#1d1d1d',
                                        border: '1px solid #333333',
                                        borderRadius: '4px',
                                        padding: '10px',
                                        paddingRight: '36px',
                                        color: 'white',
                                        fontSize: '14px',
                                        outline: 'none',
                                        appearance: 'none',
                                        cursor: 'pointer',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                                    onBlur={(e) => e.target.style.borderColor = '#333333'}
                                >
                                    <option value="IPv4">IPv4</option>
                                    <option value="IPv6">IPv6</option>
                                </select>
                                <svg 
                                    width="10" 
                                    height="6" 
                                    viewBox="0 0 10 6" 
                                    fill="none"
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <path d="M1 1L5 5L9 1" stroke="#777777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        {/* Remote IP Range Field */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{
                                color: '#d1d5db',
                                fontWeight: 500,
                                fontSize: '13px',
                                marginBottom: '6px'
                            }}>
                                Remote IP Range <span style={{ color: '#ff8080' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={newRule.remote_ip_prefix}
                                onChange={(e) => setNewRule({...newRule, remote_ip_prefix: e.target.value})}
                                style={{
                                    width: '100%',
                                    backgroundColor: '#1d1d1d',
                                    border: '1px solid #333333',
                                    borderRadius: '4px',
                                    padding: '10px',
                                    color: 'white',
                                    fontSize: '14px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                                onBlur={(e) => e.target.style.borderColor = '#333333'}
                                placeholder={newRule.ethertype === 'IPv4' ? '0.0.0.0/0' : '::/0'}
                            />
                        </div>
                    </div>

                    {/* Port Range Toggle & Fields */}
                    {(newRule.protocol === 'tcp' || newRule.protocol === 'udp') && (
                        <>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '12px'
                            }}>
                                <button 
                                    type="button"
                                    onClick={() => setUseSpecificPorts(!useSpecificPorts)}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0
                                    }}
                                >
                                    <div style={{
                                        backgroundColor: useSpecificPorts ? '#e0eacc' : '#333333',
                                        borderRadius: '9999px',
                                        position: 'relative',
                                        width: '37px',
                                        height: '20px',
                                        transition: 'background-color 0.2s'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            borderRadius: '50%',
                                            backgroundColor: 'white',
                                            width: '17px',
                                            height: '17px',
                                            top: '1.5px',
                                            left: useSpecificPorts ? '18px' : '2px',
                                            transition: 'left 0.2s'
                                        }}></div>
                                    </div>
                                    <span style={{
                                        marginLeft: '10px',
                                        color: '#d1d5db',
                                        fontSize: '13px'
                                    }}>Specify port range</span>
                                </button>
                            </div>
                            
                            {useSpecificPorts && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                    gap: '12px',
                                    marginBottom: '12px'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{
                                            color: '#d1d5db',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            marginBottom: '6px'
                                        }}>Min Port</label>
                                        <input 
                                            type="text"
                                            inputMode="numeric"
                                            style={{
                                                width: '100%',
                                                backgroundColor: '#1d1d1d',
                                                border: '1px solid #333333',
                                                borderRadius: '4px',
                                                padding: '10px',
                                                color: 'white',
                                                fontSize: '14px',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                            value={newRule.port_range_min || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                if (value === '') {
                                                    setNewRule({...newRule, port_range_min: ''});
                                                    return;
                                                }
                                                const parsed = parseInt(value, 10);
                                                if (parsed >= 1 && parsed <= 65535) {
                                                    setNewRule({...newRule, port_range_min: parsed});
                                                } else if (parsed > 65535) {
                                                    setNewRule({...newRule, port_range_min: 65535});
                                                }
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#333333';
                                                if (newRule.port_range_min && newRule.port_range_min < 1) {
                                                    setNewRule({...newRule, port_range_min: 1});
                                                }
                                            }}
                                            placeholder="1"
                                            maxLength={5}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{
                                            color: '#d1d5db',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            marginBottom: '6px'
                                        }}>Max Port</label>
                                        <input 
                                            type="text"
                                            inputMode="numeric"
                                            style={{
                                                width: '100%',
                                                backgroundColor: '#1d1d1d',
                                                border: '1px solid #333333',
                                                borderRadius: '4px',
                                                padding: '10px',
                                                color: 'white',
                                                fontSize: '14px',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                            value={newRule.port_range_max || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                if (value === '') {
                                                    setNewRule({...newRule, port_range_max: ''});
                                                    return;
                                                }
                                                const parsed = parseInt(value, 10);
                                                if (parsed >= 1 && parsed <= 65535) {
                                                    setNewRule({...newRule, port_range_max: parsed});
                                                } else if (parsed > 65535) {
                                                    setNewRule({...newRule, port_range_max: 65535});
                                                }
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#333333';
                                                if (newRule.port_range_max && newRule.port_range_max < 1) {
                                                    setNewRule({...newRule, port_range_max: 1});
                                                }
                                            }}
                                            placeholder="65535"
                                            maxLength={5}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    
                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        marginTop: '16px'
                    }}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowAddRuleModal(false);
                                setAddRuleError(null);
                                setNewRule({
                                    direction: 'ingress',
                                    protocol: 'tcp',
                                    ethertype: 'IPv4',
                                    remote_ip_prefix: '0.0.0.0/0',
                                    port_range_min: '',
                                    port_range_max: ''
                                });
                                setUseSpecificPorts(false);
                            }}
                            style={{
                                borderRadius: '4px',
                                fontWeight: 500,
                                backgroundColor: '#333333',
                                color: 'white',
                                padding: isMobile ? '12px 16px' : '8px 16px',
                                fontSize: '14px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleAddRule}
                            disabled={isAddingRule || !isVmActive}
                            style={{
                                borderRadius: '4px',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: (isAddingRule || !isVmActive) ? '#374151' : '#e0eacc',
                                color: (isAddingRule || !isVmActive) ? '#9ca3af' : 'black',
                                padding: isMobile ? '12px 16px' : '8px 16px',
                                fontSize: '14px',
                                border: 'none',
                                cursor: (isAddingRule || !isVmActive) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isAddingRule ? (
                                <>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid #6b7280',
                                        borderTopColor: 'white',
                                        borderRadius: '50%',
                                        marginRight: '8px',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Adding...
                                </>
                            ) : (
                                'Add Rule'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </>
)}                
{/* Delete Rule Confirmation Modal */}
{selectedRuleForDelete && (
    <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: isMobile ? '16px' : '0'
    }}>
        <div style={{
            backgroundColor: '#121212',
            borderRadius: '12px',
            border: '1px solid #333333',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            width: '100%',
            maxWidth: isMobile ? '100%' : '500px',
            overflow: 'hidden'
        }}>
            {/* Modal Header */}
            <div style={{
                background: 'linear-gradient(to bottom right, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05))',
                borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
                padding: isMobile ? '20px 24px' : '20px 28px'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '14px',
                    alignItems: 'center'
                }}>
                    {/* Icon */}
                    <div style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '50%',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '2px solid rgba(239, 68, 68, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <Trash2 style={{ color: '#ef4444' }} size={20} />
                    </div>

                    {/* Text Content */}
                    <div style={{ flex: 1 }}>
                        <h3 style={{
                            fontSize: isMobile ? '17px' : '18px',
                            fontWeight: '600',
                            color: '#ffffff',
                            margin: 0
                        }}>
                            Remove Security Rule?
                        </h3>
                    </div>
                </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: isMobile ? '24px' : '28px' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'start',
                    gap: '12px',
                    padding: '16px',
                    backgroundColor: 'rgba(251, 191, 36, 0.08)',
                    border: '1px solid rgba(251, 191, 36, 0.25)',
                    borderRadius: '8px'
                }}>
                    <AlertTriangle style={{ color: '#fbbf24', flexShrink: 0, marginTop: '2px' }} size={20} />
                    <p style={{
                        fontSize: '14px',
                        color: '#d1d5db',
                        margin: 0,
                        lineHeight: '1.6'
                    }}>
                        This action cannot be undone. The security rule will be removed immediately.
                    </p>
                </div>
            </div>

            {/* Modal Footer */}
            <div style={{
                borderTop: '1px solid #333333',
                padding: isMobile ? '20px 24px' : '24px 28px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                flexDirection: isMobile ? 'column-reverse' : 'row'
            }}>
                <button
                    onClick={() => setSelectedRuleForDelete(null)}
                    disabled={isDeletingRule === selectedRuleForDelete?.id}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#333333',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: isDeletingRule === selectedRuleForDelete?.id ? 'not-allowed' : 'pointer',
                        opacity: isDeletingRule === selectedRuleForDelete?.id ? 0.5 : 1,
                        transition: 'all 0.2s',
                        width: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                        if (isDeletingRule !== selectedRuleForDelete?.id) {
                            e.target.style.backgroundColor = '#444444';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#333333';
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={handleDeleteRule}
                    disabled={isDeletingRule === selectedRuleForDelete?.id}
                    style={{
                        padding: '12px 24px',
                        background: isDeletingRule === selectedRuleForDelete?.id 
                            ? '#4b5563' 
                            : 'rgba(239, 68, 68, 0.15)',
                        color: isDeletingRule === selectedRuleForDelete?.id ? '#9ca3af' : '#ef4444',
                        border: isDeletingRule === selectedRuleForDelete?.id 
                            ? 'none' 
                            : '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: isDeletingRule === selectedRuleForDelete?.id ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                        if (isDeletingRule !== selectedRuleForDelete?.id) {
                            e.target.style.background = 'rgba(239, 68, 68, 0.25)';
                            e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                            e.target.style.color = '#ff5555';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (isDeletingRule !== selectedRuleForDelete?.id) {
                            e.target.style.background = 'rgba(239, 68, 68, 0.15)';
                            e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                            e.target.style.color = '#ef4444';
                        }
                    }}
                >
                    {isDeletingRule === selectedRuleForDelete?.id ? (
                        <>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #6b7280',
                                borderTopColor: '#ffffff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            Removing...
                        </>
                    ) : (
                        <>
                            <Trash2 size={18} />
                            Remove Rule
                        </>
                    )}
                </button>
            </div>
        </div>
    </div>
)}            {/* Firewall Details Modal */}
            <FirewallDetailsModal
                isOpen={showFirewallModal}
                onClose={() => setShowFirewallModal(false)}
                firewall={selectedFirewall}
                onFirewallUpdated={() => fetchAttachedFirewalls(vmId)}
            />

            {/* UPDATED: Sophisticated Confirmation Modals */}
            
            {/* Start Modal */}
            <StartConfirmationModal
                isOpen={selectedVmForStart !== null}
                onClose={() => setSelectedVmForStart(null)}
                onConfirm={handleStartVM}
                vm={selectedVmForStart}
                isStarting={selectedVmForStart && loadingStates.starting.has(selectedVmForStart.instance_id)}
            />

            {/* Stop Modal */}
            <StopConfirmationModal
                isOpen={selectedVmForStop !== null}
                onClose={() => setSelectedVmForStop(null)}
                onConfirm={handleStopVM}
                vm={selectedVmForStop}
                isStopping={selectedVmForStop && loadingStates.stopping.has(selectedVmForStop.instance_id)}
            />

            {/* Reboot Modal */}
            <RebootConfirmationModal
                isOpen={selectedVmForReboot !== null}
                onClose={() => setSelectedVmForReboot(null)}
                onConfirm={handleRebootVM}
                vm={selectedVmForReboot}
                isRebooting={selectedVmForReboot && loadingStates.rebooting.has(selectedVmForReboot.instance_id)}
            />

            {/* Restore Modal */}
            <RestoreConfirmationModal
                isOpen={selectedVmForRestore !== null}
                onClose={() => setSelectedVmForRestore(null)}
                onConfirm={handleRestoreVM}
                vm={selectedVmForRestore}
                isRestoring={selectedVmForRestore && loadingStates.restoring.has(selectedVmForRestore.instance_id)}
            />
        </ResponsiveWrapper>
    );
};

export default VMDetailsPage;